import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, where, orderBy, limit, startAfter, getDoc, runTransaction, Timestamp, increment } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';

const PAGE_SIZE = 20;
const MAPBOX_TOKEN = 'pk.eyJ1IjoiZm9keWZlZSIsImEiOiJjbW9oajFwM2wwMDBnMnJxNDhuMTJzMGJmIn0.6uGoChaqV6fu36VllxEN5g';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const FULFILLMENT_STATUS_ORDER = {
  pending: 0,
  sent_to_kitchen: 1,
  in_preparation: 2,
  ready: 3,
  served: 4,
  out_for_delivery: 5,
  complete: 6,
};

const FULFILLMENT_STATUSES = [
  { value: 'pending', label: 'Pending', variant: 'warning', icon: 'fa-clock', color: '#856404', bg: '#fff3cd', border: '#ffeeba' },
  { value: 'sent_to_kitchen', label: 'Sent to Kitchen', variant: 'primary', icon: 'fa-utensils', color: '#004085', bg: '#cce5ff', border: '#b8daff' },
  { value: 'in_preparation', label: 'In Preparation', variant: 'info', icon: 'fa-fire', color: '#0c5460', bg: '#d1ecf1', border: '#bee5eb' },
  { value: 'ready', label: 'Ready', variant: 'success', icon: 'fa-check', color: '#155724', bg: '#d4edda', border: '#c3e6cb' },
  { value: 'served', label: 'Served', variant: 'teal', icon: 'fa-utensil-spoon', color: '#fff', bg: '#20c997', border: '#20c997' },
  { value: 'out_for_delivery', label: 'Out for Delivery', variant: 'cyan', icon: 'fa-shipping-fast', color: '#fff', bg: '#17a2b8', border: '#17a2b8' },
  { value: 'complete', label: 'Complete', variant: 'dark', icon: 'fa-check-circle', color: '#fff', bg: '#343a40', border: '#343a40' },
];

const getFulfillmentStatusMeta = (status) => {
  return FULFILLMENT_STATUSES.find(s => s.value === status) || FULFILLMENT_STATUSES[0];
};

const statusOrder = (s) => FULFILLMENT_STATUS_ORDER[s || 'pending'] ?? 0;

const getMinFulfillmentStatus = (items) => {
  if (!items || items.length === 0) return 'pending';
  let minOrder = Infinity;
  let minStatus = 'complete';
  for (const item of items) {
    const s = item.fulfillmentStatus || 'pending';
    const o = statusOrder(s);
    if (o < minOrder) {
      minOrder = o;
      minStatus = s;
    }
  }
  return minStatus;
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const searchMapboxAddress = async (query) => {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&types=address&limit=5`
    );
    if (!response.ok) return [];
    const data = await response.json();
    return (data.features || []).map(f => {
      const context = f.context || [];
      let city, state, zip, country;
      for (const ctx of context) {
        if (ctx.id?.startsWith('place')) city = ctx.text;
        else if (ctx.id?.startsWith('region')) state = ctx.text;
        else if (ctx.id?.startsWith('postcode')) zip = ctx.text;
        else if (ctx.id?.startsWith('country')) country = ctx.text;
      }
      const streetNum = f.address || '';
      const streetName = f.text || '';
      const streetAddress = streetNum ? `${streetNum} ${streetName}` : streetName;
      return {
        id: f.id,
        streetAddress: streetAddress.trim(),
        city,
        state,
        zip,
        country,
        latitude: f.geometry?.coordinates?.[1],
        longitude: f.geometry?.coordinates?.[0],
      };
    });
  } catch (e) {
    return [];
  }
};

const getSaleStatus = (sale) => {
  const isRefundTxn = sale.type === 'refund';
  const isCanceled = sale.status === 'canceled' || sale.orderStatus === 'Canceled';
  const isRefunded = sale.status === 'Complete Refund' || sale.status === 'Partially Refunded' || sale.paymentStatus === 'refunded' || sale.orderStatus === 'refunded';
  const isClosed = sale.orderStatus === 'completed' || !!sale.closeAt || isRefunded;
  if (isRefundTxn) return { label: 'Refund', variant: 'dark' };
  if (isCanceled) return { label: 'Canceled', variant: 'secondary' };
  if (isRefunded) return { label: sale.status || 'Refunded', variant: 'danger' };
  if (isClosed) return { label: 'Closed', variant: 'success' };
  return { label: 'Open', variant: 'warning' };
};

const SalesPage = () => {
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showSaleForm, setShowSaleForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  // Refund state
  const [showRefundTypeModal, setShowRefundTypeModal] = useState(false);
  const [showRefundByItemModal, setShowRefundByItemModal] = useState(false);
  const [showRefundByAmountModal, setShowRefundByAmountModal] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundItems, setRefundItems] = useState([]);
  const [refundAmountInput, setRefundAmountInput] = useState('');
  const [refundReason, setRefundReason] = useState('');
  // Split refund state
  const [showSplitRefundSelector, setShowSplitRefundSelector] = useState(false);
  const [splitChecksForRefund, setSplitChecksForRefund] = useState([]);
  const [selectedSplitForRefund, setSelectedSplitForRefund] = useState(null);

  // Filters
  const [dateRange, setDateRange] = useState('7days');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [orderTypes, setOrderTypes] = useState([]);

  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadLocations(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  // Reload sales when date filter changes
  useEffect(() => {
    if (user && selectedLocation) {
      loadSales(user.uid, selectedLocation.id, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const loadOrderTypes = async (locationId) => {
    if (!user || !locationId) return;
    try {
      const db = getFirestore();
      const otSnap = await getDocs(collection(db, 'users', user.uid, 'locations', locationId, 'orderTypes'));
      setOrderTypes(otSnap.docs.map(d => ({ id: d.id, name: d.data().name })));
    } catch (e) {
      console.error('Error loading order types:', e);
      setOrderTypes([]);
    }
  };

  const loadLocations = async (uid) => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      const locs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setLocations(locs);
      if (locs.length > 0) {
        // Load user's selected location preference
        const userDoc = await getDoc(doc(db, 'users', uid));
        const selectedId = userDoc.exists() ? userDoc.data().selectedLocationId : null;
        const defaultLoc = locs.find(l => l.id === selectedId) || locs[0];
        setSelectedLocation(defaultLoc);
        await loadOrderTypes(defaultLoc.id);
        await loadSales(uid, defaultLoc.id, true);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setLoading(false);
    }
  };

  const handleLocationSelect = async (location) => {
    setSelectedLocation(location);
    if (user) {
      // Update user's selected location in Firestore to sync with navbar
      const db = getFirestore();
      await setDoc(doc(db, 'users', user.uid), { selectedLocationId: location.id }, { merge: true });
      await loadOrderTypes(location.id);
      await loadSales(user.uid, location.id, true);
    }
  };

  const getDateRange = () => {
    const now = new Date();
    let start;
    switch (dateRange) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        break;
      case '7days':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0);
        break;
      case '30days':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0, 0);
        break;
      case 'all':
      default:
        start = null;
    }
    return start;
  };

  const loadSales = async (uid, locationId, isFirstLoad = false) => {
    if (!locationId) return [];
    if (isFirstLoad) {
      setLoading(true);
      setLastDoc(null);
      setHasMore(true);
    } else {
      if (loadingMore || !hasMore) return [];
      setLoadingMore(true);
    }

    let salesData = [];
    try {
      const db = getFirestore();
      const constraints = [
        orderBy('orderDate', 'desc'),
        limit(PAGE_SIZE)
      ];

      const dateStart = getDateRange();
      if (dateStart) {
        constraints.unshift(where('orderDate', '>=', Timestamp.fromDate(dateStart)));
      }

      if (!isFirstLoad && lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      let salesQuery = query(
        collection(db, 'users', uid, 'locations', locationId, 'sales'),
        ...constraints
      );

      const snapshot = await getDocs(salesQuery);

      if (snapshot.empty) {
        setHasMore(false);
        if (isFirstLoad) setSales([]);
      } else {
        for (const docSnapshot of snapshot.docs) {
          const saleData = docSnapshot.data();
          const itemsSnapshot = await getDocs(
            collection(db, 'users', uid, 'locations', locationId, 'sales', docSnapshot.id, 'items')
          );
          const items = itemsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

          salesData.push({
            id: docSnapshot.id,
            ...saleData,
            items,
            orderDate: saleData.orderDate?.toDate ? saleData.orderDate.toDate() : new Date(saleData.orderDate),
          });
        }

        if (isFirstLoad) {
          setSales(salesData);
        } else {
          setSales(prev => [...prev, ...salesData]);
        }

        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === PAGE_SIZE);
      }
    } catch (error) {
      console.error('Error loading sales:', error);
      if (isFirstLoad) setSales([]);
    } finally {
      if (isFirstLoad) setLoading(false);
      setLoadingMore(false);
    }
    return salesData;
  };

  const getFilteredSales = () => {
    return sales.filter(sale => {
      if (statusFilter !== 'all') {
        const status = getSaleStatus(sale);
        if (statusFilter === 'refunded' && status.variant !== 'danger' && status.variant !== 'dark') return false;
        if (statusFilter === 'closed' && status.label !== 'Closed') return false;
        if (statusFilter === 'canceled' && status.label !== 'Canceled') return false;
        if (statusFilter === 'open' && status.label !== 'Open') return false;
      }
      if (typeFilter !== 'all' && sale.orderType !== typeFilter) return false;
      if (paymentFilter !== 'all' && sale.paymentType !== paymentFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match =
          (sale.confirmationNumber || '').toLowerCase().includes(q) ||
          (sale.guestNumber || '').toLowerCase().includes(q) ||
          (sale.customerName || '').toLowerCase().includes(q) ||
          (sale.customerEmail || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
  };



  const handleLoadMore = () => {
    if (user && selectedLocation && hasMore && !loadingMore) {
      loadSales(user.uid, selectedLocation.id, false);
    }
  };

  const handleCancelSale = async (sale) => {
    if (!user || !selectedLocation) return;
    const result = await Swal.fire({
      title: 'Cancel Order?',
      text: `Are you sure you want to cancel order #${sale.confirmationNumber || sale.id}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Cancel Order',
      cancelButtonText: 'Keep Order',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Canceling...',
      text: 'Canceling order',
      imageUrl: '/fodyfee_loading.png',
      imageWidth: 80,
      imageHeight: 80,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); },
    });

    try {
      const db = getFirestore();
      const ts = new Date();
      const locId = sale.locationId;
      const saleId = sale.id;

      // 1. Update location sale doc
      const locSaleRef = doc(db, 'users', user.uid, 'locations', locId, 'sales', saleId);
      await updateDoc(locSaleRef, {
        status: 'canceled',
        paymentStatus: 'canceled',
        orderStatus: 'Canceled',
        fulfillmentStatus: 'canceled',
        modifiedAt: ts,
        updatedAt: ts,
      });

      // 2. Update root user sale doc if it exists
      try {
        const userSaleRef = doc(db, 'users', user.uid, 'sales', saleId);
        const userSaleDoc = await getDoc(userSaleRef);
        if (userSaleDoc.exists()) {
          await setDoc(userSaleRef, {
            status: 'canceled',
            paymentStatus: 'canceled',
            orderStatus: 'Canceled',
            fulfillmentStatus: 'canceled',
            modifiedAt: ts,
            updatedAt: ts,
          }, { merge: true });
        }
      } catch (userSaleErr) {
        console.warn('Could not sync cancel to user sale doc:', userSaleErr.message);
      }

      // 3. Sync to open tab if this sale has an openTabId
      const openTabId = sale.openTabId || sale.id;
      if (openTabId) {
        try {
          const functions = getFunctions();
          const cancelFn = httpsCallable(functions, 'cancelOpenTab');
          await cancelFn({ openTabId });
        } catch (tabErr) {
          console.warn('Could not sync cancel to open tab:', tabErr.message);
        }
      }

      Swal.fire('Canceled', 'Order has been canceled.', 'success');
      if (user && selectedLocation) {
        const refreshedSales = await loadSales(user.uid, selectedLocation.id, true);
        const updated = refreshedSales.find(s => s.id === sale.id);
        if (updated) setSelectedOrder(updated);
      }
      setShowOrderModal(false);
    } catch (error) {
      console.error('Error canceling sale:', error);
      Swal.fire('Error', error.message || 'Failed to cancel order', 'error');
    }
  };

  const openNewSale = () => {
    setEditingSale(null);
    setReadOnly(false);
    setShowSaleForm(true);
  };

  const openEditSale = (sale) => {
    setEditingSale(sale);
    setReadOnly(false);
    setShowSaleForm(true);
    setShowOrderModal(false);
  };

  const openViewSale = (sale) => {
    setEditingSale(sale);
    setReadOnly(true);
    setShowSaleForm(true);
    setShowOrderModal(false);
  };

  const openCheckout = (sale) => {
    setSelectedOrder(sale);
    setShowCheckout(true);
    setShowOrderModal(false);
  };

  const handleSaleSaved = () => {
    setShowSaleForm(false);
    setEditingSale(null);
    if (user && selectedLocation) {
      loadSales(user.uid, selectedLocation.id, true);
    }
  };

  const handleCheckoutComplete = () => {
    setShowCheckout(false);
    setSelectedOrder(null);
    if (user && selectedLocation) {
      loadSales(user.uid, selectedLocation.id, true);
    }
  };

  // ==================== FULFILLMENT HANDLERS ====================
  const openFulfillmentStatusPicker = async (sale) => {
    if (!user || !sale) return;
    const isCanceled = sale.status === 'canceled' || sale.orderStatus === 'Canceled';
    if (isCanceled) {
      Swal.fire('Not Allowed', 'Cannot update fulfillment status for a canceled order.', 'warning');
      return;
    }
    setShowOrderModal(false);
    const currentStatus = sale.fulfillmentStatus || 'pending';
    const statusOptions = FULFILLMENT_STATUSES.map(s => `
      <div class="form-check text-left mb-2">
        <input class="form-check-input" type="radio" name="fulfillmentStatus" id="fs_${s.value}" value="${s.value}" ${s.value === currentStatus ? 'checked' : ''}>
        <label class="form-check-label ml-2" for="fs_${s.value}">
          <span class="badge" style="background-color: ${s.bg}; color: ${s.color}; border: 1px solid ${s.border}; font-size: 0.85rem;">${s.label}</span>
        </label>
      </div>
    `).join('');

    const result = await Swal.fire({
      title: 'Update Fulfillment Status',
      html: `<div class="text-left mt-3">${statusOptions}</div>`,
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const selected = document.querySelector('input[name="fulfillmentStatus"]:checked');
        if (!selected) {
          Swal.showValidationMessage('Please select a status');
          return null;
        }
        return selected.value;
      },
    });

    if (!result.isConfirmed || !result.value) return;
    const newStatus = result.value;
    if (newStatus === currentStatus) return;

    Swal.fire({
      title: 'Updating...',
      text: 'Updating fulfillment status',
      imageUrl: '/fodyfee_loading.png',
      imageWidth: 80,
      imageHeight: 80,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); },
    });

    try {
      const db = getFirestore();
      const saleRef = doc(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id);
      const userSaleRef = doc(db, 'users', user.uid, 'sales', sale.id);
      const ts = new Date();

      // 1. Update items in location sale subcollection (setDoc with merge to avoid missing doc errors)
      const locItemsSnap = await getDocs(collection(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id, 'items'));
      const locBatch = [];
      for (const d of locItemsSnap.docs) {
        const data = d.data();
        const itemStatus = data.fulfillmentStatus || 'pending';
        if (statusOrder(itemStatus) <= statusOrder(newStatus)) {
          locBatch.push(setDoc(doc(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id, 'items', d.id), { fulfillmentStatus: newStatus, updatedAt: ts }, { merge: true }));
        }
      }
      await Promise.all(locBatch);

      // 2. Update items in user sale subcollection (setDoc with merge to avoid missing doc errors)
      const userItemsSnap = await getDocs(collection(db, 'users', user.uid, 'sales', sale.id, 'items'));
      const userBatch = [];
      for (const d of userItemsSnap.docs) {
        const data = d.data();
        const itemStatus = data.fulfillmentStatus || 'pending';
        if (statusOrder(itemStatus) <= statusOrder(newStatus)) {
          userBatch.push(setDoc(doc(db, 'users', user.uid, 'sales', sale.id, 'items', d.id), { fulfillmentStatus: newStatus, updatedAt: ts }, { merge: true }));
        }
      }
      await Promise.all(userBatch);

      // 3. Recalculate minimum status from location items
      const refreshedLocItems = await getDocs(collection(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id, 'items'));
      const allItems = refreshedLocItems.docs.map(d => d.data());
      const minStatus = getMinFulfillmentStatus(allItems);

      // 4. Update both sale docs with the recalculated minimum status
      await updateDoc(saleRef, { fulfillmentStatus: minStatus, updatedAt: ts });
      await setDoc(userSaleRef, { fulfillmentStatus: minStatus, updatedAt: ts }, { merge: true });

      // 5. Bidirectional sync: if this sale has an openTabId, update the open tab too
      const openTabId = sale.openTabId || sale.id;
      if (openTabId) {
        try {
          const tabRef = doc(db, 'users', user.uid, 'openTabs', openTabId);
          const tabDoc = await getDoc(tabRef);
          if (tabDoc.exists()) {
            const tabData = tabDoc.data();
            const tabItems = (tabData.items || []).map(item => {
              const itemStatus = item.fulfillmentStatus || 'pending';
              if (statusOrder(itemStatus) <= statusOrder(newStatus)) {
                return { ...item, fulfillmentStatus: newStatus };
              }
              return item;
            });
            await updateDoc(tabRef, {
              items: tabItems,
              fulfillmentStatus: minStatus,
              updatedAt: ts,
            });
          }
        } catch (tabErr) {
          console.warn('Could not sync to open tab:', tabErr.message);
        }
      }

      // 6. Refresh local state so the modal shows updated status if re-opened
      const refreshedSaleDoc = await getDoc(saleRef);
      if (refreshedSaleDoc.exists()) {
        const refreshedSale = { id: refreshedSaleDoc.id, ...refreshedSaleDoc.data() };
        setSelectedOrder(refreshedSale);
      }

      Swal.fire('Updated', `Fulfillment status updated to ${getFulfillmentStatusMeta(minStatus).label}.`, 'success');
      if (user && selectedLocation) {
        const refreshedSales = await loadSales(user.uid, selectedLocation.id, true);
        const updated = refreshedSales.find(s => s.id === sale.id);
        if (updated) setSelectedOrder(updated);
      }
      // Close any open modals
      setShowOrderModal(false);
      setShowSaleForm(false);
      setEditingSale(null);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.message || 'Failed to update fulfillment status', 'error');
    }
  };

  // ==================== REFUND HANDLERS ====================
  const loadSplitChecksForRefund = async (sale) => {
    if (!user || !sale?.id) return [];
    try {
      const db = getFirestore();
      let splitRef = collection(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id, 'splitChecks');
      let snap = await getDocs(splitRef);
      if (snap.empty) {
        splitRef = collection(db, 'users', user.uid, 'sales', sale.id, 'splitChecks');
        snap = await getDocs(splitRef);
      }
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.error('Error loading split checks for refund:', e);
      return [];
    }
  };

  const openRefundFlow = async (sale) => {
    setShowOrderModal(false);
    setRefundLoading(true);
    try {
      // Load fresh sale data
      const db = getFirestore();
      const saleDoc = await getDoc(doc(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id));
      if (!saleDoc.exists()) {
        Swal.fire('Error', 'Sale not found', 'error');
        return;
      }
      const freshSale = { id: saleDoc.id, ...saleDoc.data() };

      const isRefundTxn = freshSale.type === 'refund';
      const isCompleteRefund = freshSale.status === 'Complete Refund' || (freshSale.refundedAmount || 0) >= (freshSale.total || 0);
      const isCanceled = freshSale.status === 'canceled' || freshSale.orderStatus === 'Canceled';
      const refundStrategy = freshSale.refundStrategy;

      if (isRefundTxn) {
        Swal.fire('Cannot Refund', 'This is a refund transaction and cannot be refunded.', 'warning');
        return;
      }
      if (isCanceled) {
        Swal.fire('Cannot Refund', 'This order has been canceled.', 'warning');
        return;
      }
      if (isCompleteRefund) {
        Swal.fire('Cannot Refund', 'This order has been completely refunded.', 'warning');
        return;
      }

      setSelectedOrder(freshSale);

      // Check for split checks
      const splits = await loadSplitChecksForRefund(freshSale);
      const paidSplits = splits.filter(s => s.isPaid === true && !s.refundedAmount);
      if (paidSplits.length > 0) {
        setSplitChecksForRefund(splits);
        setShowSplitRefundSelector(true);
        setRefundLoading(false);
        return;
      }

      // Strategy lock: skip type selector if already chosen
      if (refundStrategy === 'by_item') {
        const items = await loadSaleItemsForRefund(freshSale);
        setRefundItems(items.map(item => ({ ...item, selected: false, refunded: item.refunded || false })));
        setShowRefundByItemModal(true);
      } else if (refundStrategy === 'by_amount') {
        setRefundAmountInput('');
        setShowRefundByAmountModal(true);
      } else {
        setShowRefundTypeModal(true);
      }
    } catch (e) {
      console.error('Error loading fresh sale data:', e);
      // Fallback to passed sale
      setSelectedOrder(sale);
      setShowRefundTypeModal(true);
    } finally {
      setRefundLoading(false);
    }
  };

  const closeRefundModals = () => {
    setShowRefundTypeModal(false);
    setShowRefundByItemModal(false);
    setShowRefundByAmountModal(false);
    setShowSplitRefundSelector(false);
    setRefundItems([]);
    setRefundAmountInput('');
    setRefundReason('');
    setSelectedSplitForRefund(null);
    setSplitChecksForRefund([]);
    setSelectedOrder(null);
  };

  const loadSaleItemsForRefund = async (sale) => {
    if (!user || !sale) return [];
    try {
      const db = getFirestore();
      const itemsSnap = await getDocs(collection(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id, 'items'));
      return itemsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.error('Error loading sale items:', e);
      return [];
    }
  };

  const openRefundByItem = async () => {
    setShowRefundTypeModal(false);
    const items = await loadSaleItemsForRefund(selectedOrder);
    setRefundItems(items.map(item => ({ ...item, selected: false, refunded: item.refunded || false })));
    setShowRefundByItemModal(true);
  };

  const openRefundByAmount = () => {
    setShowRefundTypeModal(false);
    setRefundAmountInput('');
    setShowRefundByAmountModal(true);
  };

  const toggleRefundItem = (itemId) => {
    setRefundItems(prev => prev.map(item =>
      item.id === itemId && !item.refunded ? { ...item, selected: !item.selected } : item
    ));
  };

  const calculateRefundTotals = (selectedItems, originalSale) => {
    const subtotal = selectedItems.reduce((sum, item) => sum + (item.total || (item.price || 0) * (item.qty || 0)), 0);
    const allItemsSelected = selectedItems.length === refundItems.filter(i => !i.refunded).length;

    let tax = 0;
    let tip = 0;
    let convenienceFee = 0;
    if (selectedSplitForRefund) {
      // For split refunds, use the split's tax and tip
      tax = allItemsSelected ? (selectedSplitForRefund.tax || 0) : 0;
      tip = allItemsSelected ? (selectedSplitForRefund.tip || 0) : 0;
      convenienceFee = allItemsSelected ? (selectedSplitForRefund.convenienceFee || 0) : 0;
    } else {
      const taxRate = selectedLocation?.taxRate || 0;
      tax = subtotal * (taxRate / 100);
      tip = allItemsSelected ? (originalSale.tip || 0) : 0;
      // Convenience Fee is a % of the whole transaction (not per-item), so —
      // same as tip — it only gets refunded on a full item refund.
      convenienceFee = allItemsSelected ? (originalSale.convenienceFee || 0) : 0;
    }

    const total = subtotal + tax + tip + convenienceFee;
    return { subtotal, tax, tip, convenienceFee, total, allItemsSelected };
  };

  const processRefund = async ({ refundType, refundData }) => {
    if (!user || !selectedOrder || !selectedLocation) return;
    setRefundLoading(true);

    try {
      const db = getFirestore();
      const saleRef = doc(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales', selectedOrder.id);
      const userSaleRef = doc(db, 'users', user.uid, 'sales', selectedOrder.id);
      const refundId = doc(collection(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales')).id;
      const userRefundRef = doc(db, 'users', user.uid, 'sales', refundId);
      const locRefundRef = doc(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales', refundId);

      const timestamp = new Date();
      const isFullRefund = refundType === 'by_item' ? refundData.allItemsSelected : refundData.allItemsSelected;
      const newStatus = isFullRefund ? 'Complete Refund' : 'Partially Refunded';
      const currentRefundedAmount = (selectedOrder.refundedAmount || 0);
      const newRefundedAmount = Math.min(currentRefundedAmount + (refundData.total || 0), selectedOrder.total || 0);

      // Pre-fetch user-level items to map by productId (auto-generated IDs differ from location-level)
      let userItemMap = {};
      if (refundType === 'by_item' && refundData.selectedItems?.length > 0) {
        const userItemsSnap = await getDocs(
          collection(db, 'users', user.uid, 'sales', selectedOrder.id, 'items')
        );
        for (const d of userItemsSnap.docs) {
          const data = d.data();
          if (data.productId && !userItemMap[data.productId]) {
            userItemMap[data.productId] = d.id;
          }
        }
      }

      // Build refund document
      const refundDoc = {
        id: refundId,
        userId: user.uid,
        locationId: selectedOrder.locationId,
        originalSaleId: selectedOrder.id,
        type: 'refund',
        refundType,
        salesChannel: selectedOrder.salesChannel || 'POS',
        customerId: selectedOrder.customerId || null,
        fodyesUid: selectedOrder.fodyesUid || null,
        customerName: selectedOrder.customerName || 'Walk-in Customer',
        customerEmail: selectedOrder.customerEmail || '',
        customerPhone: selectedOrder.customerPhone || '',
        paymentType: selectedOrder.paymentType || 'cash',
        status: 'completed',
        paymentStatus: 'refunded',
        orderStatus: 'refunded',
        itemsQty: -(refundData.itemsQty || 0),
        subtotal: -(refundData.subtotal || 0),
        tax: -(refundData.tax || 0),
        tip: -(refundData.tip || 0),
        convenienceFee: -(refundData.convenienceFee || 0),
        discount: -(selectedOrder.discount || 0),
        total: -(refundData.total || 0),
        stripePaymentIntentId: selectedOrder.stripePaymentIntentId || null,
        stripeRefundId: null,
        notes: refundReason || `Refund for sale ${selectedOrder.confirmationNumber || selectedOrder.id}`,
        orderDate: timestamp,
        createdAt: timestamp,
        updatedAt: timestamp,
      };

      const runFirestoreRefundTx = async (stripeRefundId) => {
        if (stripeRefundId) refundDoc.stripeRefundId = stripeRefundId;
        // The root sale may not exist for console-created (mirror-only) sales
        // made before the id-unification fix — check first so the root update
        // never aborts the whole transaction with NOT_FOUND.
        const userSaleSnap = await getDoc(userSaleRef);
        const rootSaleExists = userSaleSnap.exists();
        await runTransaction(db, async (transaction) => {
          transaction.update(saleRef, {
            status: newStatus,
            paymentStatus: newStatus === 'Complete Refund' ? 'refunded' : 'partially_refunded',
            refundedAmount: newRefundedAmount,
            refundStrategy: refundType,
            updatedAt: timestamp,
          });
          if (rootSaleExists) {
            transaction.update(userSaleRef, {
              status: newStatus,
              paymentStatus: newStatus === 'Complete Refund' ? 'refunded' : 'partially_refunded',
              refundedAmount: newRefundedAmount,
              refundStrategy: refundType,
              updatedAt: timestamp,
            });
          }
          transaction.set(locRefundRef, refundDoc);
          transaction.set(userRefundRef, refundDoc);

          if (refundType === 'by_item' && refundData.selectedItems?.length > 0) {
            for (const item of refundData.selectedItems) {
              const itemRef = doc(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales', selectedOrder.id, 'items', item.id);
              transaction.update(itemRef, { refunded: true, refundedAt: timestamp });

              const userItemId = userItemMap[item.productId];
              if (userItemId) {
                const userItemRef = doc(db, 'users', user.uid, 'sales', selectedOrder.id, 'items', userItemId);
                transaction.update(userItemRef, { refunded: true, refundedAt: timestamp });
              }

              const refundItem = { ...item, qty: -(item.qty || 0), total: -(item.total || 0), price: item.price || 0, refunded: true };
              const refundItemRef = doc(collection(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales', refundId, 'items'));
              const userRefundItemRef = doc(collection(db, 'users', user.uid, 'sales', refundId, 'items'));
              transaction.set(refundItemRef, refundItem);
              transaction.set(userRefundItemRef, refundItem);
            }
          }
        });
      };

      let stripeRefundId = null;
      if (!selectedOrder.stripePaymentIntentId) {
        await runFirestoreRefundTx();
      } else {
        const functions = getFunctions();
        const processStripeRefundFn = httpsCallable(functions, 'processStripeRefund');
        const stripeResult = await processStripeRefundFn({
          paymentIntentId: selectedOrder.stripePaymentIntentId,
          amount: refundData.total,
          reason: 'requested_by_customer',
        });
        const stripeStatus = stripeResult.data.status;
        if (stripeStatus !== 'succeeded') {
          throw new Error(`Stripe refund status: ${stripeStatus}`);
        }
        stripeRefundId = stripeResult.data.refundId;
        await runFirestoreRefundTx(stripeRefundId);
      }

      Swal.fire({
        title: 'Refund Processed',
        text: `${newStatus} — ${formatCurrency(refundData.total)}`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });

      // Open refund receipt print dialog
      printRefundReceipt(selectedOrder, refundData, newStatus, stripeRefundId);

      closeRefundModals();
      loadSales(user.uid, selectedLocation.id, true);
    } catch (error) {
      console.error('Refund error:', error);
      Swal.fire('Error', error.message || 'Failed to process refund', 'error');
    } finally {
      setRefundLoading(false);
    }
  };

  const processSplitRefund = async ({ split, refundType, refundData }) => {
    if (!user || !selectedOrder || !split) return;
    setRefundLoading(true);

    try {
      const db = getFirestore();
      const saleRef = doc(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales', selectedOrder.id);
      const userSaleRef = doc(db, 'users', user.uid, 'sales', selectedOrder.id);
      const refundId = doc(collection(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales')).id;
      const userRefundRef = doc(db, 'users', user.uid, 'sales', refundId);
      const locRefundRef = doc(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales', refundId);

      const timestamp = new Date();
      const splitTotalWithTip = (split.total || 0) + (split.tip || 0);
      const currentSaleRefunded = (selectedOrder.refundedAmount || 0);
      const newSaleRefundedAmount = Math.min(currentSaleRefunded + (refundData.total || 0), selectedOrder.total || 0);

      // Check if all paid splits are now refunded
      const updatedSplits = splitChecksForRefund.map(s =>
        s.id === split.id ? { ...s, refundedAmount: refundData.total, refundedAt: timestamp.toISOString() } : s
      );
      const allPaidSplits = updatedSplits.filter(s => s.isPaid === true);
      const allRefunded = allPaidSplits.length > 0 && allPaidSplits.every(s => (s.refundedAmount || 0) > 0);
      const newStatus = allRefunded ? 'Complete Refund' : 'Partially Refunded';

      // Build refund document
      const refundDoc = {
        id: refundId,
        userId: user.uid,
        locationId: selectedOrder.locationId,
        originalSaleId: selectedOrder.id,
        splitId: split.id,
        splitLabel: split.label,
        type: 'refund',
        refundType,
        salesChannel: selectedOrder.salesChannel || 'POS',
        customerId: selectedOrder.customerId || null,
        fodyesUid: selectedOrder.fodyesUid || null,
        customerName: selectedOrder.customerName || 'Walk-in Customer',
        customerEmail: selectedOrder.customerEmail || '',
        customerPhone: selectedOrder.customerPhone || '',
        paymentType: split.paymentMethod || 'cash',
        status: 'completed',
        paymentStatus: 'refunded',
        orderStatus: 'refunded',
        itemsQty: -(refundData.itemsQty || 0),
        subtotal: -(refundData.subtotal || 0),
        tax: -(refundData.tax || 0),
        tip: -(refundData.tip || 0),
        discount: 0,
        total: -(refundData.total || 0),
        stripePaymentIntentId: split.stripePaymentIntentId || null,
        stripeRefundId: null,
        notes: refundReason || `Refund for ${split.label} — sale ${selectedOrder.confirmationNumber || selectedOrder.id}`,
        orderDate: timestamp,
        createdAt: timestamp,
        updatedAt: timestamp,
      };

      const runFirestoreSplitRefundTx = async (stripeRefundId) => {
        if (stripeRefundId) refundDoc.stripeRefundId = stripeRefundId;
        // Tolerate legacy mirror-only sales: a missing root doc must never
        // abort the refund transaction (same guard as processRefund).
        const userSaleSnap = await getDoc(userSaleRef);
        const rootSaleExists = userSaleSnap.exists();
        await runTransaction(db, async (transaction) => {
          // Update sale main doc
          transaction.update(saleRef, {
            status: newStatus,
            paymentStatus: newStatus === 'Complete Refund' ? 'refunded' : 'partially_refunded',
            refundedAmount: newSaleRefundedAmount,
            updatedAt: timestamp,
          });
          if (rootSaleExists) {
            transaction.update(userSaleRef, {
              status: newStatus,
              paymentStatus: newStatus === 'Complete Refund' ? 'refunded' : 'partially_refunded',
              refundedAmount: newSaleRefundedAmount,
              updatedAt: timestamp,
            });
          }

          // Update split doc
          const splitRef = doc(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales', selectedOrder.id, 'splitChecks', split.id);
          const userSplitRef = doc(db, 'users', user.uid, 'sales', selectedOrder.id, 'splitChecks', split.id);
          transaction.update(splitRef, {
            refundedAmount: refundData.total,
            refundedAt: timestamp,
            updatedAt: timestamp,
          });
          if (rootSaleExists) {
            transaction.update(userSplitRef, {
              refundedAmount: refundData.total,
              refundedAt: timestamp,
              updatedAt: timestamp,
            });
          }

          // Create refund doc
          transaction.set(locRefundRef, refundDoc);
          transaction.set(userRefundRef, refundDoc);

          // If refund by item, create refund items
          if (refundType === 'by_item' && refundData.selectedItems?.length > 0) {
            for (const item of refundData.selectedItems) {
              const refundItem = { ...item, qty: -(item.qty || 0), total: -(item.total || 0), price: item.price || 0, refunded: true };
              const refundItemRef = doc(collection(db, 'users', user.uid, 'locations', selectedOrder.locationId, 'sales', refundId, 'items'));
              const userRefundItemRef = doc(collection(db, 'users', user.uid, 'sales', refundId, 'items'));
              transaction.set(refundItemRef, refundItem);
              transaction.set(userRefundItemRef, refundItem);
            }
          }
        });
      };

      let stripeRefundId = null;
      if (!split.stripePaymentIntentId) {
        await runFirestoreSplitRefundTx();
      } else {
        const functions = getFunctions();
        const processStripeRefundFn = httpsCallable(functions, 'processStripeRefund');
        const stripeResult = await processStripeRefundFn({
          paymentIntentId: split.stripePaymentIntentId,
          amount: refundData.total,
          reason: 'requested_by_customer',
        });
        const stripeStatus = stripeResult.data.status;
        if (stripeStatus !== 'succeeded') {
          throw new Error(`Stripe refund status: ${stripeStatus}`);
        }
        stripeRefundId = stripeResult.data.refundId;
        await runFirestoreSplitRefundTx(stripeRefundId);
      }

      Swal.fire({
        title: 'Refund Processed',
        text: `${newStatus} — ${split.label}: ${formatCurrency(refundData.total)}`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });

      printRefundReceipt(selectedOrder, refundData, newStatus, stripeRefundId);

      closeRefundModals();
      loadSales(user.uid, selectedLocation.id, true);
    } catch (error) {
      console.error('Split refund error:', error);
      Swal.fire('Error', error.message || 'Failed to process split refund', 'error');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleRefundByItem = () => {
    const selected = refundItems.filter(i => i.selected && !i.refunded);
    if (selected.length === 0) {
      Swal.fire('Error', 'Please select at least one item to refund', 'error');
      return;
    }
    const totals = calculateRefundTotals(selected, selectedOrder);

    if (selectedSplitForRefund) {
      const splitTotal = (selectedSplitForRefund.total || 0) + (selectedSplitForRefund.tip || 0);
      const splitRefunded = selectedSplitForRefund.refundedAmount || 0;
      const splitRemaining = Math.max(0, splitTotal - splitRefunded);
      if (totals.total > splitRemaining) {
        Swal.fire('Error', `Refund amount cannot exceed ${selectedSplitForRefund.label} remaining ${formatCurrency(splitRemaining)}`, 'error');
        return;
      }
      processSplitRefund({
        split: selectedSplitForRefund,
        refundType: 'by_item',
        refundData: { ...totals, selectedItems: selected, itemsQty: selected.reduce((s, i) => s + (i.qty || 0), 0) },
      });
      return;
    }

    const alreadyRefunded = selectedOrder.refundedAmount || 0;
    const remaining = (selectedOrder.total || 0) - alreadyRefunded;
    // Compare in whole CENTS to avoid float-dust false negatives.
    if (Math.round(totals.total * 100) > Math.round(remaining * 100)) {
      Swal.fire('Error', `Refund amount cannot exceed remaining ${formatCurrency(remaining)}`, 'error');
      return;
    }
    processRefund({
      refundType: 'by_item',
      refundData: { ...totals, selectedItems: selected, itemsQty: selected.reduce((s, i) => s + (i.qty || 0), 0) },
    });
  };

  const handleRefundByAmount = () => {
    const amount = parseFloat(refundAmountInput);
    if (isNaN(amount) || amount <= 0) {
      Swal.fire('Error', 'Please enter a valid refund amount', 'error');
      return;
    }

    if (selectedSplitForRefund) {
      const splitTotal = (selectedSplitForRefund.total || 0) + (selectedSplitForRefund.tip || 0);
      const splitRefunded = selectedSplitForRefund.refundedAmount || 0;
      const splitRemaining = Math.max(0, splitTotal - splitRefunded);
      // Compare in whole CENTS to avoid float-dust false negatives.
      if (Math.round(amount * 100) > Math.round(splitRemaining * 100)) {
        Swal.fire('Error', `Refund amount cannot exceed ${selectedSplitForRefund.label} remaining ${formatCurrency(splitRemaining)}`, 'error');
        return;
      }
      const isFull = amount >= splitRemaining;
      processSplitRefund({
        split: selectedSplitForRefund,
        refundType: 'by_amount',
        refundData: { total: amount, allItemsSelected: isFull, itemsQty: 0, subtotal: amount, tax: 0, tip: 0 },
      });
      return;
    }

    const alreadyRefunded = selectedOrder.refundedAmount || 0;
    const maxTotal = (selectedOrder.total || 0) - alreadyRefunded;
    // Compare in whole CENTS — the sale total can carry float dust
    // (30.569999…), which made entering the displayed full amount ($30.57)
    // fail the check with "cannot exceed remaining $30.57".
    if (Math.round(amount * 100) > Math.round(maxTotal * 100)) {
      Swal.fire('Error', `Refund amount cannot exceed remaining ${formatCurrency(maxTotal)}`, 'error');
      return;
    }
    const isFull = (amount + alreadyRefunded) >= (selectedOrder.total || 0);
    processRefund({
      refundType: 'by_amount',
      refundData: { total: amount, allItemsSelected: isFull, itemsQty: 0, subtotal: amount, tax: 0, tip: 0 },
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const printRefundReceipt = (sale, refundData, newStatus, stripeRefundId) => {
    const selectedItems = refundData.selectedItems || [];
    const businessName = sale.businessName || 'Restaurant';
    const locationName = sale.locationName || '';
    const now = new Date();
    const itemsHtml = selectedItems.length > 0
      ? selectedItems.map(item => `
        <tr>
          <td style="padding:4px 0;text-align:left;">${item.productName || 'Item'} x${item.qty || 1}</td>
          <td style="padding:4px 0;text-align:right;">($${(item.total || 0).toFixed(2)})</td>
        </tr>
      `).join('')
      : '';

    const html = `
      <html>
      <head>
        <title>Refund Receipt</title>
        <style>
          body { font-family: 'Courier New', monospace; width: 320px; margin: 0 auto; padding: 16px; background: #fff; }
          .center { text-align: center; }
          .header { font-weight: bold; font-size: 18px; margin-bottom: 4px; }
          .subheader { font-size: 12px; color: #666; margin-bottom: 8px; }
          .label { font-size: 12px; }
          .value { font-size: 12px; }
          .section { margin: 8px 0; }
          .divider { border-top: 1px dashed #000; margin: 8px 0; }
          .total-row { display: flex; justify-content: space-between; font-weight: bold; }
          .refund-badge { background: ${newStatus === 'Complete Refund' ? '#ffebee' : '#fff3e0'}; color: ${newStatus === 'Complete Refund' ? '#c62828' : '#e65100'}; padding: 6px; border-radius: 4px; text-align: center; font-weight: bold; margin-top: 8px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="center header">${businessName}</div>
        <div class="center subheader">${locationName}</div>
        <div class="divider"></div>
        <div style="text-align:center;font-weight:bold;color:#c62828;font-size:14px;margin:8px 0;">REFUND RECEIPT</div>
        <div class="section">
          <div class="label">Confirmation #${sale.confirmationNumber || sale.id}</div>
          <div class="label">Date: ${now.toLocaleString()}</div>
          <div class="label">Customer: ${sale.customerName || 'Walk-in'}</div>
          <div class="label">Payment: ${sale.paymentType || 'cash'}</div>
          ${stripeRefundId ? `<div class="label" style="color:#666;font-size:10px;">Stripe Refund ID: ${stripeRefundId}</div>` : ''}
        </div>
        ${selectedItems.length > 0 ? `
          <div class="divider"></div>
          <div style="font-weight:bold;font-size:12px;margin-bottom:4px;">REFUNDED ITEMS</div>
          <table>${itemsHtml}</table>
        ` : ''}
        <div class="divider"></div>
        <div class="total-row"><span class="label">Subtotal:</span><span class="value">$${(refundData.subtotal || 0).toFixed(2)}</span></div>
        <div class="total-row"><span class="label">Tax:</span><span class="value">$${(refundData.tax || 0).toFixed(2)}</span></div>
        ${(refundData.tip || 0) !== 0 ? `<div class="total-row"><span class="label">Tip:</span><span class="value">$${(refundData.tip || 0).toFixed(2)}</span></div>` : ''}
        ${(refundData.discount || 0) !== 0 ? `<div class="total-row"><span class="label">Discount:</span><span class="value">-$${(refundData.discount || 0).toFixed(2)}</span></div>` : ''}
        <div class="divider"></div>
        <div class="total-row" style="color:#c62828;"><span class="label">Refund Total:</span><span class="value">$${(refundData.total || 0).toFixed(2)}</span></div>
        <div style="margin-top:8px;">
          <div class="total-row"><span class="label">Original Total:</span><span class="value">$${(sale.total || 0).toFixed(2)}</span></div>
          <div class="total-row"><span class="label">Previously Refunded:</span><span class="value">$${(sale.refundedAmount || 0).toFixed(2)}</span></div>
          <div class="total-row"><span class="label">New Balance:</span><span class="value">$${Math.max(0, (sale.total || 0) - (sale.refundedAmount || 0) - (refundData.total || 0)).toFixed(2)}</span></div>
        </div>
        <div class="refund-badge">${newStatus}</div>
        <div class="divider"></div>
        <div class="center" style="font-size:10px;color:#666;margin-top:8px;">Thank you</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=400,height=700');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  const isOrderClosed = (sale) => {
    const status = getSaleStatus(sale);
    // Block canceled and refund transactions. Allow partial refunds to continue refunding.
    // Only block Complete Refund (danger + not Partially Refunded)
    const isCompleteRefund = status.label === 'Complete Refund';
    return status.label === 'Canceled' || status.label === 'Closed' || status.variant === 'dark' || isCompleteRefund;
  };

  if (loading && sales.length === 0) {
    return (
      <AdminLayout title="Sales">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Sales">
      <SetupGuard prerequisites={['location', 'floorplan', 'workzones', 'product']}>
      <div className="sales-page">
        {/* Location Selector */}
        {locations.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex overflow-auto pb-2" style={{ gap: '12px' }}>
                {locations.map((location) => (
                  <button
                    key={location.id}
                    className={`btn ${selectedLocation?.id === location.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{
                      minWidth: '100px',
                      borderRadius: '12px',
                      backgroundColor: selectedLocation?.id === location.id ? '#ff6b35' : undefined,
                      borderColor: selectedLocation?.id === location.id ? '#ff6b35' : undefined,
                    }}
                    onClick={() => handleLocationSelect(location)}
                  >
                    <i className="fas fa-store mr-2"></i>
                    {location.companyName || location.locationName}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Add Sale Button */}
        <div className="row mb-3">
          <div className="col-12 text-right">
            <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={openNewSale}>
              <i className="fas fa-plus mr-2"></i>New Sale
            </button>
          </div>
        </div>

        {/* Filters */}
        {selectedLocation && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                <select className="form-control form-control-sm" style={{ width: 'auto' }} value={dateRange} onChange={e => setDateRange(e.target.value)}>
                  <option value="7days">Last 7 Days</option>
                  <option value="today">Today</option>
                  <option value="30days">Last 30 Days</option>
                  <option value="all">All Time</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: 'auto' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="all">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="canceled">Canceled</option>
                  <option value="refunded">Refunded</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: 'auto' }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                  <option value="all">All Types</option>
                  {orderTypes.map((ot, i) => (
                    <option key={i} value={ot.name}>{ot.name}</option>
                  ))}
                </select>
                <select className="form-control form-control-sm" style={{ width: 'auto' }} value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}>
                  <option value="all">All Payments</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="tap-to-pay">Tap to Pay</option>
                </select>
                <input type="text" className="form-control form-control-sm" style={{ width: '200px' }} placeholder="Search order # or customer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                {(searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || paymentFilter !== 'all') && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearchQuery(''); setStatusFilter('all'); setTypeFilter('all'); setPaymentFilter('all'); }}>
                    <i className="fas fa-times mr-1"></i>Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sales List */}
        {locations.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-store-slash fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">No locations configured</h4>
            <button className="btn btn-primary" onClick={() => navigate('/admin/locations/new')}>
              <i className="fas fa-plus mr-2"></i>Add Location
            </button>
          </div>
        ) : sales.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-receipt fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">No sales yet</h4>
            <button className="btn btn-primary mt-3" onClick={openNewSale}>
              <i className="fas fa-plus mr-2"></i>New Sale
            </button>
          </div>
        ) : getFilteredSales().length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-filter fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">No sales match your filters</h4>
            <button className="btn btn-outline-secondary mt-3" onClick={() => { setSearchQuery(''); setStatusFilter('all'); setTypeFilter('all'); setPaymentFilter('all'); }}>
              <i className="fas fa-times mr-2"></i>Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2 px-2">
              <small className="text-muted">
                Showing {getFilteredSales().length}{hasMore ? '+' : ''} of {sales.length} loaded{hasMore ? ` (${PAGE_SIZE} per page)` : ''}
                {dateRange !== 'all' && ` · ${dateRange === '7days' ? 'Last 7 Days' : dateRange === 'today' ? 'Today' : 'Last 30 Days'}`}
              </small>
            </div>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Guest #</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Items</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredSales().map((sale) => {
                    const isRefundTxn = sale.type === 'refund';
                    return (
                    <tr key={sale.id} style={{ cursor: 'pointer' }} onClick={() => { setSelectedOrder(sale); setShowOrderModal(true); }}>
                      <td><span className="font-weight-bold">#{sale.confirmationNumber || sale.id?.substring(0, 8)}</span></td>
                      <td><span className="font-weight-bold text-info">{sale.guestNumber || '—'}</span></td>
                      <td>
                        {isRefundTxn ? (
                          <div>
                            <div>Refund — Original: <span className="font-weight-bold">#{sale.originalSaleId?.substring(0, 8)}</span></div>
                            <small className="text-muted">{sale.customerName || 'Walk-in'}</small>
                          </div>
                        ) : (
                          <div>
                            <div>{sale.customerName || 'Walk-in'}</div>
                            {sale.customerEmail && <small className="text-muted">{sale.customerEmail}</small>}
                          </div>
                        )}
                      </td>
                      <td>{formatDate(sale.orderDate)}</td>
                      <td>
                        {sale.orderType && <span className="badge badge-info">{sale.orderType}</span>}
                        {/* Uber Direct marker — staff can tell at a glance this
                            order goes out with an Uber courier (auto-dispatch
                            when it turns Ready), not a manual delivery. */}
                        {sale.deliveryProvider === 'uber_direct' && (
                          <span
                            className="badge ml-1"
                            style={{ background: '#fff3ee', color: '#ff5722', border: '1px solid rgba(255,87,34,0.4)', fontSize: '0.65rem' }}
                            title={sale.deliveryStatus ? `Courier: ${sale.deliveryStatus.replace(/_/g, ' ')}` : 'Courier dispatched when the order turns Ready'}
                          >
                            <i className="fas fa-motorcycle mr-1"></i>
                            {sale.deliveryStatus ? sale.deliveryStatus.replace(/_/g, ' ') : 'Uber'}
                          </span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const status = getSaleStatus(sale);
                          return (
                            <div className="d-flex flex-column" style={{ gap: 4 }}>
                              <span className={`badge badge-${status.variant}`}>{status.label}</span>
                              {!isRefundTxn && sale.fulfillmentStatus && sale.fulfillmentStatus !== 'complete' && (
                                (() => {
                                  const meta = getFulfillmentStatusMeta(sale.fulfillmentStatus);
                                  const isCustom = meta.variant === 'teal' || meta.variant === 'cyan';
                                  return (
                                    <span className={`badge badge-${isCustom ? '' : meta.variant}`}
                                      style={{ fontSize: '0.65rem', ...(isCustom ? { backgroundColor: meta.bg, color: meta.color } : {}) }}>
                                      {meta.label}
                                    </span>
                                  );
                                })()
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td>{sale.itemsQty || sale.items?.length || 0}</td>
                      <td>
                        {(() => {
                          const isRefundTxn = sale.type === 'refund';
                          const isRefunded = sale.status === 'Complete Refund' || sale.status === 'Partially Refunded';
                          const refundedAmount = sale.refundedAmount || 0;
                          const remaining = Math.max(0, (sale.total || 0) - refundedAmount);
                          return (
                            <div>
                              {isRefundTxn ? (
                                <span className="font-weight-bold" style={{ color: '#ff6b35' }}>{formatCurrency(sale.total)}</span>
                              ) : isRefunded ? (
                                <>
                                  <span className="font-weight-bold text-muted" style={{ textDecoration: 'line-through' }}>{formatCurrency(sale.total)}</span>
                                  <div className="d-flex flex-column" style={{ fontSize: '0.75rem' }}>
                                    <span className="text-danger">Refunded: {formatCurrency(refundedAmount)}</span>
                                    {sale.status === 'Partially Refunded' && remaining > 0 && (
                                      <span className="text-success">Remaining: {formatCurrency(remaining)}</span>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <span className="font-weight-bold" style={{ color: '#ff6b35' }}>{formatCurrency(sale.total)}</span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div className="text-center mt-3">
                <button className="btn btn-outline-primary" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? <><i className="fas fa-spinner fa-spin mr-2"></i>Loading...</> : <><i className="fas fa-plus mr-2"></i>Load More</>}
                </button>
              </div>
            )}
          </>
        )}

        {/* Order Options Modal */}
        {showOrderModal && selectedOrder && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title font-weight-bold">Order #{selectedOrder.confirmationNumber}{selectedOrder.guestNumber ? ` · Guest #${selectedOrder.guestNumber}` : ''}</h5>
                  <button type="button" className="close" onClick={() => setShowOrderModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body p-0">
                  {(() => {
                    const isRefundTxn = selectedOrder.type === 'refund';
                    const isCompleteRefund = selectedOrder.status === 'Complete Refund' || (selectedOrder.refundedAmount || 0) >= (selectedOrder.total || 0);
                    const isCanceled = selectedOrder.status === 'canceled' || selectedOrder.orderStatus === 'Canceled';
                    const canRefund = !isRefundTxn && !isCompleteRefund && !isCanceled;
                    return (
                      <>
                        {isOrderClosed(selectedOrder) && (
                          <button className="list-group-item list-group-item-action py-3" onClick={() => openViewSale(selectedOrder)}>
                            <i className="fas fa-eye text-primary mr-2"></i>View Details
                          </button>
                        )}
                        {!isOrderClosed(selectedOrder) && (
                          <>
                            <button className="list-group-item list-group-item-action py-3" onClick={() => openEditSale(selectedOrder)}>
                              <i className="fas fa-edit text-primary mr-2"></i>Edit Order
                            </button>
                            <button className="list-group-item list-group-item-action py-3" onClick={() => openCheckout(selectedOrder)}>
                              <i className="fas fa-credit-card text-primary mr-2"></i>Checkout
                            </button>
                          </>
                        )}
                        {/* Fulfillment Actions for any non-complete, non-closed, non-refund order */}
                        {!isRefundTxn && !isOrderClosed(selectedOrder) && selectedOrder.fulfillmentStatus !== 'complete' && (
                          <button className="list-group-item list-group-item-action py-3" onClick={() => openFulfillmentStatusPicker(selectedOrder)}>
                            <i className="fas fa-utensils text-primary mr-2"></i>Fulfillment Actions
                          </button>
                        )}
                        {/* Cancel Order for any non-closed, non-refund order */}
                        {!isOrderClosed(selectedOrder) && !isRefundTxn && (
                          <button className="list-group-item list-group-item-action py-3" onClick={() => handleCancelSale(selectedOrder)}>
                            <i className="fas fa-times-circle text-danger mr-2"></i>Cancel Order
                          </button>
                        )}
                        {canRefund && (
                          <button className="list-group-item list-group-item-action py-3" onClick={() => openRefundFlow(selectedOrder)}>
                            <i className="fas fa-undo text-warning mr-2"></i>Refund
                          </button>
                        )}
                        {!canRefund && !isRefundTxn && isCompleteRefund && (
                          <button className="list-group-item list-group-item-action py-3" disabled>
                            <i className="fas fa-ban text-muted mr-2"></i>Already Refunded
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sale Form Modal */}
        {showSaleForm && selectedLocation && (
          <SaleFormModal
            location={selectedLocation}
            sale={editingSale}
            readOnly={readOnly}
            user={user}
            onSave={handleSaleSaved}
            onClose={() => { setShowSaleForm(false); setEditingSale(null); }}
            onFulfillmentUpdate={openFulfillmentStatusPicker}
          />
        )}

        {/* Checkout Modal */}
        {showCheckout && selectedOrder && selectedLocation && (
          <CheckoutModal
            sale={selectedOrder}
            location={selectedLocation}
            user={user}
            onComplete={handleCheckoutComplete}
            onClose={() => { setShowCheckout(false); setSelectedOrder(null); }}
          />
        )}

        {/* Refund Type Modal */}
        {showRefundTypeModal && selectedOrder && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title font-weight-bold">Refund</h5>
                  <button type="button" className="close" onClick={closeRefundModals}><span>&times;</span></button>
                </div>
                <div className="modal-body">
                  <div className="text-center mb-3">
                    <div className="text-muted small">Order #{selectedOrder.confirmationNumber || selectedOrder.id?.substring(0, 8)}{selectedOrder.guestNumber ? ` · Guest #${selectedOrder.guestNumber}` : ''}</div>
                    <div className="font-weight-bold" style={{ color: '#ff6b35' }}>{formatCurrency(selectedOrder.total)}</div>
                    {(selectedOrder.refundedAmount || 0) > 0 && (
                      <>
                        <div className="text-danger small">Already refunded: {formatCurrency(selectedOrder.refundedAmount)}</div>
                        <div className="text-success small">Remaining: {formatCurrency((selectedOrder.total || 0) - (selectedOrder.refundedAmount || 0))}</div>
                      </>
                    )}
                  </div>
                  <p className="text-muted small mb-2">Select refund type:</p>
                  <button className="list-group-item list-group-item-action py-3" onClick={openRefundByItem}>
                    <i className="fas fa-list text-primary mr-2"></i>Refund by Item
                  </button>
                  <button className="list-group-item list-group-item-action py-3" onClick={openRefundByAmount}>
                    <i className="fas fa-dollar-sign text-primary mr-2"></i>Refund by Amount
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Refund by Item Modal */}
        {showRefundByItemModal && selectedOrder && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 500, maxHeight: '85vh' }}>
              <div className="modal-content" style={{ borderRadius: '20px', border: 'none', maxHeight: '85vh', overflowY: 'auto' }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title font-weight-bold">Refund by Item</h5>
                  <button type="button" className="close" onClick={closeRefundModals}><span>&times;</span></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted small">Select items to refund:</p>
                  {refundItems.map(item => {
                    const isRefunded = item.refunded;
                    return (
                      <div
                        key={item.id}
                        className={`d-flex align-items-center mb-2 p-2 ${isRefunded ? 'text-muted' : 'cursor-pointer'}`}
                        style={{
                          background: item.selected ? '#fff3e0' : '#f8f9fa',
                          borderRadius: 12,
                          border: item.selected ? '2px solid #ff6b35' : '2px solid transparent',
                          cursor: isRefunded ? 'not-allowed' : 'pointer',
                          opacity: isRefunded ? 0.6 : 1,
                        }}
                        onClick={() => !isRefunded && toggleRefundItem(item.id)}
                      >
                        <div className="custom-control custom-checkbox mr-2">
                          <input type="checkbox" className="custom-control-input" checked={item.selected} readOnly disabled={isRefunded} />
                          <label className="custom-control-label"></label>
                        </div>
                        <div className="flex-grow-1">
                          <div className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{item.productName}</div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {formatCurrency(item.price)} × {item.qty} = {formatCurrency(item.total || (item.price || 0) * (item.qty || 0))}
                          </div>
                        </div>
                        {isRefunded && <span className="badge badge-light">Already Refunded</span>}
                      </div>
                    );
                  })}

                  {(() => {
                    const selected = refundItems.filter(i => i.selected && !i.refunded);
                    const totals = selected.length > 0 ? calculateRefundTotals(selected, selectedOrder) : null;
                    return totals ? (
                      <div className="mt-3 p-3" style={{ background: '#f8f9fa', borderRadius: 12 }}>
                        <div className="d-flex justify-content-between"><span className="text-muted">Items</span><span className="font-weight-bold">{selected.length}</span></div>
                        <div className="d-flex justify-content-between"><span className="text-muted">Subtotal</span><span className="font-weight-bold">{formatCurrency(totals.subtotal)}</span></div>
                        {totals.tax > 0 && <div className="d-flex justify-content-between"><span className="text-muted">Tax</span><span className="font-weight-bold">{formatCurrency(totals.tax)}</span></div>}
                        {totals.tip > 0 && <div className="d-flex justify-content-between"><span className="text-muted">Tip</span><span className="font-weight-bold">{formatCurrency(totals.tip)}</span></div>}
                        {totals.convenienceFee > 0 && <div className="d-flex justify-content-between"><span className="text-muted">Convenience Fee</span><span className="font-weight-bold">{formatCurrency(totals.convenienceFee)}</span></div>}
                        <div className="d-flex justify-content-between mt-2 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                          <span className="font-weight-bold">Refund Total</span>
                          <span className="font-weight-bold" style={{ color: '#ff6b35' }}>{formatCurrency(totals.total)}</span>
                        </div>
                        {totals.allItemsSelected && <div className="text-success small mt-1"><i className="fas fa-check-circle mr-1"></i>Full refund (including tip)</div>}
                      </div>
                    ) : null;
                  })()}

                  <div className="form-group mt-3">
                    <label className="small text-muted">Refund Reason (optional)</label>
                    <textarea className="form-control form-control-sm" rows={2} value={refundReason} onChange={e => setRefundReason(e.target.value)} placeholder="Reason for refund..." />
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button className="btn btn-lg btn-light" style={{ borderRadius: 12 }} onClick={closeRefundModals}>Cancel</button>
                  <button
                    className="btn btn-lg btn-primary font-weight-bold"
                    style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 12 }}
                    disabled={refundLoading || refundItems.filter(i => i.selected && !i.refunded).length === 0}
                    onClick={handleRefundByItem}
                  >
                    {refundLoading ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-undo mr-2"></i>}
                    Refund
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Refund by Amount Modal */}
        {showRefundByAmountModal && selectedOrder && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title font-weight-bold">
                    {selectedSplitForRefund ? `Refund ${selectedSplitForRefund.label || 'Split'}` : 'Refund by Amount'}
                  </h5>
                  <button type="button" className="close" onClick={closeRefundModals}><span>&times;</span></button>
                </div>
                <div className="modal-body">
                  {(() => {
                    const splitTotal = selectedSplitForRefund ? (selectedSplitForRefund.total || 0) + (selectedSplitForRefund.tip || 0) : 0;
                    const splitRefunded = selectedSplitForRefund ? (selectedSplitForRefund.refundedAmount || 0) : 0;
                    const splitRemaining = Math.max(0, splitTotal - splitRefunded);
                    const orderRemaining = Math.max(0, (selectedOrder.total || 0) - (selectedOrder.refundedAmount || 0));
                    return (
                      <div className="text-center mb-3">
                        <div className="text-muted small">{selectedSplitForRefund ? `${selectedSplitForRefund.label || 'Split'} Total` : 'Original Total'}</div>
                        <div className="font-weight-bold" style={{ fontSize: '1.5rem', color: '#ff6b35' }}>
                          {formatCurrency(selectedSplitForRefund ? splitTotal : selectedOrder.total)}
                        </div>
                        {selectedSplitForRefund ? (
                          <>
                            <div className="text-danger small mt-1">Already refunded: {formatCurrency(splitRefunded)}</div>
                            <div className="text-success small">Remaining: {formatCurrency(splitRemaining)}</div>
                          </>
                        ) : (selectedOrder.refundedAmount || 0) > 0 && (
                          <>
                            <div className="text-danger small mt-1">Already refunded: {formatCurrency(selectedOrder.refundedAmount)}</div>
                            <div className="text-success small">Remaining: {formatCurrency(orderRemaining)}</div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  <div className="form-group">
                    <label className="small text-muted">
                      Refund Amount (max {formatCurrency(selectedSplitForRefund
                        ? Math.max(0, (selectedSplitForRefund.total || 0) + (selectedSplitForRefund.tip || 0) - (selectedSplitForRefund.refundedAmount || 0))
                        : Math.max(0, (selectedOrder.total || 0) - (selectedOrder.refundedAmount || 0))
                      )})
                    </label>
                    <input type="number" step="0.01" className="form-control" placeholder="0.00"
                      value={refundAmountInput} onChange={e => setRefundAmountInput(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="small text-muted">Refund Reason (optional)</label>
                    <textarea className="form-control form-control-sm" rows={2} value={refundReason} onChange={e => setRefundReason(e.target.value)} placeholder="Reason for refund..." />
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button className="btn btn-lg btn-light" style={{ borderRadius: 12 }} onClick={closeRefundModals}>Cancel</button>
                  <button
                    className="btn btn-lg btn-primary font-weight-bold"
                    style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 12 }}
                    disabled={refundLoading || !refundAmountInput}
                    onClick={handleRefundByAmount}
                  >
                    {refundLoading ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-undo mr-2"></i>}
                    Refund
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Split Refund Selector Modal */}
        {showSplitRefundSelector && selectedOrder && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title font-weight-bold">Refund Split Check</h5>
                  <button type="button" className="close" onClick={closeRefundModals}><span>&times;</span></button>
                </div>
                <div className="modal-body">
                  <div className="text-center mb-3">
                    <div className="text-muted small">Order #{selectedOrder.confirmationNumber || selectedOrder.id?.substring(0, 8)}</div>
                    <div className="font-weight-bold" style={{ color: '#ff6b35' }}>{formatCurrency(selectedOrder.total)}</div>
                    {(selectedOrder.refundedAmount || 0) > 0 && (
                      <>
                        <div className="text-danger small">Already refunded: {formatCurrency(selectedOrder.refundedAmount)}</div>
                        <div className="text-success small">Remaining: {formatCurrency((selectedOrder.total || 0) - (selectedOrder.refundedAmount || 0))}</div>
                      </>
                    )}
                  </div>
                  <p className="text-muted small mb-2">Select a paid check to refund:</p>
                  {splitChecksForRefund.filter(s => s.isPaid === true).map((split, idx) => {
                    const isRefunded = (split.refundedAmount || 0) > 0;
                    const splitTotal = (split.total || 0) + (split.tip || 0);
                    return (
                      <div key={split.id || idx} className={`list-group-item list-group-item-action py-3 ${isRefunded ? 'text-muted' : ''}`}
                        style={{ opacity: isRefunded ? 0.6 : 1, cursor: isRefunded ? 'not-allowed' : 'pointer' }}
                        onClick={() => {
                          if (isRefunded) return;
                          setSelectedSplitForRefund(split);
                          setShowSplitRefundSelector(false);
                          if (split.type === 'items') {
                            const splitItems = (split.items || []).map((it, i) => ({
                              id: `split_${split.id}_item_${i}`,
                              productId: it.productId || '',
                              productName: it.productName || 'Item',
                              price: it.price || 0,
                              qty: it.qty || 1,
                              total: it.total || ((it.price || 0) * (it.qty || 1)),
                              refunded: false,
                              selected: false,
                            }));
                            setRefundItems(splitItems);
                            setShowRefundByItemModal(true);
                          } else {
                            setRefundAmountInput('');
                            setShowRefundByAmountModal(true);
                          }
                        }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>{split.label || `Check ${idx + 1}`}</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {split.type === 'equal' ? 'Equal' : split.type === 'items' ? 'By Items' : 'By Amount'}
                              {split.paymentMethod && ` • ${split.paymentMethod}`}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-weight-bold">{formatCurrency(splitTotal)}</div>
                            {isRefunded && <span className="badge badge-light">Already Refunded</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </SetupGuard>
    </AdminLayout>
  );
};

/* ==========================================
   SALE FORM MODAL
   ========================================== */
const SaleFormModal = ({ location, sale, readOnly, user, onSave, onClose, onFulfillmentUpdate }) => {
  const [orderTypes, setOrderTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [items, setItems] = useState(sale?.items || []);
  const [loading, setLoading] = useState(true);

  // Only treat the tip as "percent-based" when the sale explicitly says so
  // AND has a usable percent value. Older/other sales (e.g. handheld POS
  // direct "Charge" sales created before tipPercent/tipIsPercent were
  // persisted) omit these fields entirely — in that case we must fall back
  // to the raw dollar `sale.tip`, otherwise the form would look for a
  // non-existent tipPercent and silently display $0.00 even though the sale
  // was correctly tipped.
  const initialTipIsPercent = sale?.tipIsPercent === true && !!sale?.tipPercent;

  const [formData, setFormData] = useState({
    customerName: sale?.customerName || '',
    customerEmail: sale?.customerEmail || '',
    customerPhone: sale?.customerPhone || '',
    selectedCustomerId: sale?.customerId || null,
    fodyesUid: sale?.fodyesUid || null,
    isGuestCustomer: sale?.isGuestCustomer || !sale?.customerId,
    orderType: sale?.orderType || '',
    paymentType: sale?.paymentType || 'cash',
    tipPercent: sale?.tipPercent || 0,
    tipIsPercent: initialTipIsPercent,
    tipAmount: sale?.tip || 0,
    discount: sale?.discount || 0,
    deliveryAddress: sale?.deliveryAddress || '',
    deliveryCity: sale?.deliveryCity || '',
    deliveryState: sale?.deliveryState || '',
    deliveryZipCode: sale?.deliveryZipCode || '',
    deliveryCountry: sale?.deliveryCountry || '',
    deliveryLatitude: sale?.deliveryLatitude || null,
    deliveryLongitude: sale?.deliveryLongitude || null,
  });

  const [tipInput, setTipInput] = useState(
    initialTipIsPercent ? (sale.tipPercent || '').toString() : (sale?.tip || 0).toString()
  );
  const [discountInput, setDiscountInput] = useState((sale?.discount || '').toString());

  // Modals state
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showProductOptions, setShowProductOptions] = useState(false);
  const [showQuantityDialog, setShowQuantityDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOptionsState, setSelectedOptionsState] = useState({});
  const [extraFees, setExtraFees] = useState({});
  const [refunds, setRefunds] = useState([]);
  const [splitChecks, setSplitChecks] = useState([]);

  useEffect(() => {
    loadFormData();
  }, []);

  useEffect(() => {
    if (readOnly && sale?.id && user) {
      loadRefundsForSale();
      loadSplitChecksForSale();
    }
  }, [readOnly, sale?.id, user]);

  const loadRefundsForSale = async () => {
    if (!sale?.id || !user) return;
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'users', user.uid, 'sales'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const related = all.filter(r => r.originalSaleId === sale.id && r.type === 'refund');
      setRefunds(related);
    } catch (e) {
      console.error('Error loading refunds:', e);
    }
  };

  const loadSplitChecksForSale = async () => {
    if (!sale?.id || !user) return;
    try {
      const db = getFirestore();
      // Try location sales path first, then user sales path
      let splitRef = collection(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id, 'splitChecks');
      let snap = await getDocs(splitRef);
      if (snap.empty) {
        splitRef = collection(db, 'users', user.uid, 'sales', sale.id, 'splitChecks');
        snap = await getDocs(splitRef);
      }
      const splits = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSplitChecks(splits);
    } catch (e) {
      console.error('Error loading split checks:', e);
      setSplitChecks([]);
    }
  };

  const loadFormData = async () => {
    if (!user) return;
    try {
      const db = getFirestore();

      // Order types
      const otSnap = await getDocs(collection(db, 'users', user.uid, 'locations', location.id, 'orderTypes'));
      setOrderTypes(otSnap.docs.map(d => ({ id: d.id, name: d.data().name })));

      // Categories & products
      const catSnap = await getDocs(collection(db, 'users', user.uid, 'categories'));
      const cats = catSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const allProducts = [];
      for (const cat of cats) {
        const prodSnap = await getDocs(collection(db, 'users', user.uid, 'categories', cat.id, 'products'));
        prodSnap.docs.forEach(d => allProducts.push({ id: d.id, categoryId: cat.id, ...d.data() }));
      }
      setCategories(cats);
      setProducts(allProducts);

      // Product options
      const optSnap = await getDocs(collection(db, 'users', user.uid, 'productOptions'));
      setProductOptions(optSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Customers
      const custSnap = await getDocs(collection(db, 'users', user.uid, 'customers'));
      setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // If editing, load customer addresses
      if (sale?.customerId) {
        const addrSnap = await getDocs(
          collection(db, 'users', user.uid, 'customers', sale.customerId, 'addresses')
        );
        setCustomerAddresses(addrSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
  const taxRate = location.taxRate || 0;
  const tax = subtotal * (taxRate / 100);
  const discount = parseFloat(discountInput) || 0;
  const calculatedTip = formData.tipIsPercent
    ? subtotal * ((parseFloat(tipInput) || 0) / 100)
    : (parseFloat(tipInput) || 0);
  // Convenience Fee is computed automatically by the payment flow (card
  // only), never edited manually here — just preserve whatever was already
  // on the sale so editing an order doesn't silently drop it from the total.
  const convenienceFee = sale?.convenienceFee || 0;
  const total = subtotal + tax + calculatedTip + convenienceFee - discount;
  const totalItems = items.reduce((sum, item) => sum + (item.qty || 0), 0);

  const handleSelectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      selectedCustomerId: customer.id,
      fodyesUid: customer.fodyesUid || null,
      isGuestCustomer: false,
      customerName: customer.name,
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      deliveryAddress: customer.address || '',
      deliveryCity: customer.city || '',
      deliveryState: customer.state || '',
      deliveryZipCode: customer.zipCode || '',
      deliveryCountry: customer.country || '',
      deliveryLatitude: customer.latitude || null,
      deliveryLongitude: customer.longitude || null,
    }));
    // Load customer addresses
    const loadAddr = async () => {
      const db = getFirestore();
      const addrSnap = await getDocs(
        collection(db, 'users', user.uid, 'customers', customer.id, 'addresses')
      );
      setCustomerAddresses(addrSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    loadAddr();
    setShowCustomerSelector(false);
  };

  const handleSetGuest = () => {
    setFormData(prev => ({
      ...prev,
      selectedCustomerId: null,
      fodyesUid: null,
      isGuestCustomer: true,
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      deliveryAddress: '',
      deliveryCity: '',
      deliveryState: '',
      deliveryZipCode: '',
      deliveryCountry: '',
      deliveryLatitude: null,
      deliveryLongitude: null,
    }));
    setCustomerAddresses([]);
    setShowCustomerSelector(false);
  };

  const handleProductTap = (product) => {
    setSelectedProduct(product);
    if (product.optionIds?.length > 0) {
      setSelectedOptionsState({});
      setExtraFees({});
      setShowProductOptions(true);
    } else {
      setSelectedOptionsState({});
      setExtraFees({});
      setShowQuantityDialog(true);
    }
    setShowProductSelector(false);
  };

  const handleAddProduct = (product, qty, options, extraFeeTotal, instructions) => {
    const itemPrice = (product.price || 0) + extraFeeTotal;
    const existingIndex = items.findIndex(
      item => item.productId === product.id && JSON.stringify(item.options || []) === JSON.stringify(options)
    );

    const category = categories.find(c => c.id === product.categoryId);
    const workzoneId = category?.workzonesByLocation?.[location?.id] || null;

    if (existingIndex >= 0) {
      const newItems = [...items];
      const newQty = (newItems[existingIndex].qty || 0) + qty;
      newItems[existingIndex] = {
        ...newItems[existingIndex],
        qty: newQty,
        total: itemPrice * newQty,
      };
      setItems(newItems);
    } else {
      setItems(prev => [...prev, {
        productId: product.id,
        productName: product.name,
        productDescription: product.description || '',
        productImage: product.imageUrl || null,
        categoryId: product.categoryId || null,
        categoryName: product.category || '',
        workzoneId: workzoneId,
        qty,
        price: itemPrice,
        total: itemPrice * qty,
        options,
        instructions: instructions || null,
        date: new Date(),
        fulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
      }]);
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    if (newItems[index].qty > 1) {
      newItems[index] = { ...newItems[index], qty: newItems[index].qty - 1, total: newItems[index].price * (newItems[index].qty - 1) };
    } else {
      newItems.splice(index, 1);
    }
    setItems(newItems);
  };

  const handleUpdateItemFulfillment = async (itemIndex) => {
    if (!sale?.id || !user) return;
    const isCanceled = sale.status === 'canceled' || sale.orderStatus === 'Canceled';
    if (isCanceled) {
      Swal.fire('Not Allowed', 'Cannot update fulfillment status for a canceled order.', 'warning');
      return;
    }
    const item = items[itemIndex];
    if (!item?.id) {
      Swal.fire('Error', 'Item does not have a Firestore ID. Please reload the page.', 'error');
      return;
    }
    const currentStatus = item.fulfillmentStatus || 'pending';
    const statusOptions = FULFILLMENT_STATUSES.map(s => `
      <div class="form-check text-left mb-2">
        <input class="form-check-input" type="radio" name="itemFulfillmentStatus" id="ifs_${s.value}" value="${s.value}" ${s.value === currentStatus ? 'checked' : ''}>
        <label class="form-check-label ml-2" for="ifs_${s.value}">
          <span class="badge" style="background-color: ${s.bg}; color: ${s.color}; border: 1px solid ${s.border}; font-size: 0.85rem;">${s.label}</span>
        </label>
      </div>
    `).join('');

    const result = await Swal.fire({
      title: `Update Item: ${item.productName}`,
      html: `<div class="text-left mt-3">${statusOptions}</div>`,
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const selected = document.querySelector('input[name="itemFulfillmentStatus"]:checked');
        if (!selected) {
          Swal.showValidationMessage('Please select a status');
          return null;
        }
        return selected.value;
      },
    });

    if (!result.isConfirmed || !result.value) return;
    const newStatus = result.value;
    if (newStatus === currentStatus) return;

    Swal.fire({
      title: 'Updating...',
      text: 'Updating item fulfillment status',
      imageUrl: '/fodyfee_loading.png',
      imageWidth: 80,
      imageHeight: 80,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); },
    });

    try {
      const db = getFirestore();
      const saleRef = doc(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id);
      const userSaleRef = doc(db, 'users', user.uid, 'sales', sale.id);
      const ts = new Date();

      // 1. Update this specific item in both subcollections (setDoc with merge to avoid missing doc errors)
      const locItemRef = doc(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id, 'items', item.id);
      const userItemRef = doc(db, 'users', user.uid, 'sales', sale.id, 'items', item.id);
      await setDoc(locItemRef, { fulfillmentStatus: newStatus, updatedAt: ts }, { merge: true });
      await setDoc(userItemRef, { fulfillmentStatus: newStatus, updatedAt: ts }, { merge: true });

      // 2. Read all items back and recalculate minimum status
      const refreshedLocItems = await getDocs(collection(db, 'users', user.uid, 'locations', sale.locationId, 'sales', sale.id, 'items'));
      const allItems = refreshedLocItems.docs.map(d => d.data());
      const minStatus = getMinFulfillmentStatus(allItems);

      // 3. Update main docs with recalculated minimum
      await updateDoc(saleRef, { fulfillmentStatus: minStatus, updatedAt: ts });
      await setDoc(userSaleRef, { fulfillmentStatus: minStatus, updatedAt: ts }, { merge: true });

      // 4. Bidirectional sync: if this sale has an openTabId, update the open tab too
      const itemOpenTabId = sale.openTabId || sale.id;
      if (itemOpenTabId) {
        try {
          const tabRef = doc(db, 'users', user.uid, 'openTabs', itemOpenTabId);
          const tabDoc = await getDoc(tabRef);
          if (tabDoc.exists()) {
            const tabData = tabDoc.data();
            const tabItems = (tabData.items || []).map(it => {
              if (it.productId === item.productId && (it.fulfillmentStatus || 'pending') === (item.fulfillmentStatus || 'pending')) {
                return { ...it, fulfillmentStatus: newStatus };
              }
              return it;
            });
            await updateDoc(tabRef, {
              items: tabItems,
              fulfillmentStatus: minStatus,
              updatedAt: ts,
            });
          }
        } catch (tabErr) {
          console.warn('Could not sync to open tab:', tabErr.message);
        }
      }

      // 5. Update local state and refresh parent
      const newItems = [...items];
      newItems[itemIndex] = { ...newItems[itemIndex], fulfillmentStatus: newStatus };
      setItems(newItems);
      // Also update the local sale object so the banner refreshes
      if (sale) {
        sale.fulfillmentStatus = minStatus;
        sale.items = newItems;
      }
      onSave();

      Swal.fire('Updated', `Item status updated to ${getFulfillmentStatusMeta(newStatus).label}. Order status is now ${getFulfillmentStatusMeta(minStatus).label}.`, 'success');
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.message || 'Failed to update item status', 'error');
    }
  };

  const generateConfirmationNumber = async () => {
    const db = getFirestore();
    const locRef = doc(db, 'users', user.uid, 'locations', location.id);
    const { confirmationNumber, guestNumber } = await runTransaction(db, async (transaction) => {
      const locDoc = await transaction.get(locRef);
      let ordersPosition = locDoc.data()?.ordersPosition || 0;
      const ordersCorrelative = locDoc.data()?.ordersCorrelative || 1;
      ordersPosition++;
      if (ordersPosition >= ordersCorrelative * 100) ordersPosition = 1;
      transaction.update(locRef, { ordersPosition });
      const confirmationNumber = ordersPosition.toString().padStart(4, '0');
      return { confirmationNumber, guestNumber: confirmationNumber };
    });
    return { confirmationNumber, guestNumber };
  };

  const handleSave = async () => {
    if (!formData.customerName.trim()) {
      Swal.fire('Error', 'Customer name is required', 'error');
      return;
    }
    if (!formData.orderType) {
      Swal.fire('Error', 'Please select an order type', 'error');
      return;
    }
    if (items.length === 0) {
      Swal.fire('Error', 'Please add at least one item', 'error');
      return;
    }

    setLoading(true);
    try {
      const db = getFirestore();
      let confirmationNumber, guestNumber;
      if (sale?.confirmationNumber) {
        confirmationNumber = sale.confirmationNumber;
        guestNumber = sale.guestNumber || confirmationNumber;
      } else {
        const generated = await generateConfirmationNumber();
        confirmationNumber = generated.confirmationNumber;
        guestNumber = generated.guestNumber;
      }

      const saleData = {
        locationId: location.id,
        locationName: location.companyName,
        orderDate: sale?.orderDate || new Date(),
        customerId: formData.selectedCustomerId,
        fodyesUid: formData.fodyesUid || null,
        customerEmail: formData.customerEmail.trim(),
        customerName: formData.customerName.trim(),
        customerPhone: formData.customerPhone.trim(),
        orderType: formData.orderType,
        isGuestCustomer: formData.isGuestCustomer,
        confirmationNumber,
        guestNumber,
        modifiedAt: new Date(),
        orderStatus: sale?.orderStatus || 'open',
        closeAt: sale?.closeAt || null,
        paymentType: formData.paymentType,
        itemsQty: totalItems,
        subtotal,
        tax,
        tip: calculatedTip,
        tipPercent: formData.tipIsPercent ? (parseFloat(tipInput) || 0) : 0,
        tipIsPercent: formData.tipIsPercent,
        convenienceFee,
        discount,
        total,
        deliveryAddress: formData.deliveryAddress,
        deliveryCity: formData.deliveryCity,
        deliveryState: formData.deliveryState,
        deliveryZipCode: formData.deliveryZipCode,
        deliveryCountry: formData.deliveryCountry,
        deliveryLatitude: formData.deliveryLatitude,
        deliveryLongitude: formData.deliveryLongitude,
        fulfillmentStatus: sale?.fulfillmentStatus || location?.defaultFulfillmentStatus || 'pending',
      };

      let saleId;
      if (sale) {
        await updateDoc(doc(db, 'users', user.uid, 'locations', location.id, 'sales', sale.id), saleData);
        saleId = sale.id;
      } else {
        const docRef = doc(collection(db, 'users', user.uid, 'locations', location.id, 'sales'));
        await setDoc(docRef, saleData);
        saleId = docRef.id;
      }

      // Root mirror (SAME id, merge — a plain set on edit would clobber
      // root-only fields like status/paymentStatus/stripePaymentIntentId)
      await setDoc(doc(db, 'users', user.uid, 'sales', saleId), saleData, { merge: true });

      // Replace items
      const itemsCollection = collection(db, 'users', user.uid, 'locations', location.id, 'sales', saleId, 'items');
      const existingItems = await getDocs(itemsCollection);
      await Promise.all(existingItems.docs.map(d => deleteDoc(doc(db, 'users', user.uid, 'locations', location.id, 'sales', saleId, 'items', d.id))));
      await Promise.all(items.map(item => setDoc(doc(collection(db, 'users', user.uid, 'locations', location.id, 'sales', saleId, 'items')), item)));

      // Root items mirror
      const rootItemsCollection = collection(db, 'users', user.uid, 'sales', saleId, 'items');
      const existingRootItems = await getDocs(rootItemsCollection);
      await Promise.all(existingRootItems.docs.map(d => deleteDoc(d.ref)));
      await Promise.all(items.map(item => setDoc(doc(rootItemsCollection), item)));

      // Save address to customer if delivery
      const isDelivery = formData.orderType.toLowerCase() === 'delivery' || formData.orderType.toLowerCase().includes('delivery');
      if (isDelivery && formData.selectedCustomerId && formData.deliveryAddress) {
        const shouldSave = await Swal.fire({
          title: 'Save Address?',
          text: 'Do you want to save this address to the customer for future orders?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        });
        if (shouldSave.isConfirmed) {
          await setDoc(doc(collection(db, 'users', user.uid, 'customers', formData.selectedCustomerId, 'addresses')), {
            address: formData.deliveryAddress,
            city: formData.deliveryCity,
            state: formData.deliveryState,
            zipCode: formData.deliveryZipCode,
            country: formData.deliveryCountry,
            latitude: formData.deliveryLatitude,
            longitude: formData.deliveryLongitude,
            createdAt: new Date(),
          });
        }
      }

      onSave();
      Swal.fire('Success', sale ? 'Sale updated' : 'Sale created', 'success');
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'Failed to save sale', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReceipt = async () => {
    if (!formData.customerEmail) {
      Swal.fire('Error', 'Customer email is required', 'error');
      return;
    }
    setLoading(true);
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'sendReceiptEmail');
      await callable({
        saleId: sale?.id,
        customerEmail: formData.customerEmail,
        customerName: formData.customerName,
        locationName: location.companyName,
        subtotal,
        tax,
        tip: calculatedTip,
        discount,
        total,
        items: items.map(i => ({
          productName: i.productName,
          productDescription: i.productDescription,
          options: i.options?.map(o => typeof o === 'string' ? o : o.itemName || o.name) || [],
          instructions: i.instructions,
          qty: i.qty,
          price: i.price,
          total: i.total,
        })),
      });
      Swal.fire('Success', `Receipt sent to ${formData.customerEmail}`, 'success');
    } catch (e) {
      Swal.fire('Error', `Failed to send receipt: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = () => {
    const printWindow = window.open('', '_blank', 'width=320,height=600');
    if (!printWindow) {
      Swal.fire('Error', 'Please allow popups to print the receipt', 'error');
      return;
    }

    const dateStr = sale?.orderDate
      ? new Date(sale.orderDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const itemsHtml = items.map(item => {
      const optionsStr = item.options?.length > 0
        ? `<div style="font-size:10px;color:#555;padding-left:8px;">+ ${item.options.map(o => typeof o === 'string' ? o : o.itemName || o.name).join(', ')}</div>`
        : '';
      const instructionsStr = item.instructions ? `<div style="font-size:10px;color:#777;padding-left:8px;"><i>Note: ${item.instructions}</i></div>` : '';
      return `
        <div style="margin-bottom:6px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;">
            <div style="flex:1;">
              <span style="font-weight:bold;">${item.qty}x</span> ${item.productName}
            </div>
            <div style="text-align:right;min-width:60px;">${formatCurrency(item.total)}</div>
          </div>
          ${optionsStr}
          ${instructionsStr}
        </div>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Receipt #${sale?.confirmationNumber || ''}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              width: 80mm;
              max-width: 80mm;
              margin: 0 auto;
              padding: 8px;
              box-sizing: border-box;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .divider {
              border-top: 1px dashed #000;
              margin: 8px 0;
            }
            .line {
              display: flex;
              justify-content: space-between;
            }
            .total {
              font-size: 14px;
              font-weight: bold;
            }
            .logo {
              max-width: 60mm;
              max-height: 50px;
              object-fit: contain;
              margin-bottom: 4px;
            }
            .no-print {
              margin-top: 20px;
              text-align: center;
            }
            .no-print button {
              padding: 10px 24px;
              font-size: 14px;
              cursor: pointer;
              background: #ff6b35;
              color: #fff;
              border: none;
              border-radius: 6px;
            }
          </style>
        </head>
        <body>
          ${location.logoUrl ? `<div class="center"><img src="${location.logoUrl}" class="logo" onerror="this.style.display='none'" /></div>` : ''}
          <div class="center bold" style="font-size:14px;">${location.companyName || location.locationName || 'Restaurant'}</div>
          ${location.address ? `<div class="center">${location.address}</div>` : ''}
          ${location.city || location.state || location.zip ? `<div class="center">${[location.city, location.state, location.zip].filter(Boolean).join(', ')}</div>` : ''}
          ${location.phone ? `<div class="center">Tel: ${location.phone}</div>` : ''}
          <div class="divider"></div>

          <div class="center bold">RECEIPT</div>
          <div class="line"><span>Order #:</span><span>${sale?.confirmationNumber || sale?.id?.substring(0, 8) || 'N/A'}</span></div>
          <div class="line"><span>Date:</span><span>${dateStr}</span></div>
          <div class="line"><span>Type:</span><span>${sale?.orderType || formData.orderType || 'N/A'}</span></div>
          <div class="line"><span>Payment:</span><span>${(sale?.paymentType || formData.paymentType || 'cash').toUpperCase()}</span></div>
          <div class="divider"></div>

          <div class="bold">Customer</div>
          <div>${sale?.customerName || formData.customerName || 'Walk-in'}</div>
          ${(sale?.customerPhone || formData.customerPhone) ? `<div>Tel: ${sale?.customerPhone || formData.customerPhone}</div>` : ''}
          ${(sale?.customerEmail || formData.customerEmail) ? `<div>${sale?.customerEmail || formData.customerEmail}</div>` : ''}
          <div class="divider"></div>

          <div class="bold">Items</div>
          ${itemsHtml}
          <div class="divider"></div>

          <div class="line"><span>Subtotal</span><span>${formatCurrency(subtotal)}</span></div>
          <div class="line"><span>Tax (${location.taxRate || 0}%)</span><span>${formatCurrency(tax)}</span></div>
          <div class="line"><span>Tip</span><span>${formatCurrency(calculatedTip)}</span></div>
          ${discount > 0 ? `<div class="line"><span>Discount</span><span>-${formatCurrency(discount)}</span></div>` : ''}
          <div class="divider"></div>
          <div class="line total"><span>TOTAL</span><span>${formatCurrency(total)}</span></div>

          <div class="divider"></div>
          <div class="center" style="font-size:11px;margin-top:4px;">
            <div>Thank you for your visit!</div>
            <div>Please come again</div>
          </div>

          <div class="no-print">
            <button onclick="window.print();">Print Receipt</button>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const relevantOptions = selectedProduct
    ? productOptions.filter(opt => selectedProduct.optionIds?.includes(opt.id))
    : [];

  const totalExtraFee = Object.values(extraFees).reduce((sum, fee) => sum + fee, 0);
  const finalPrice = (selectedProduct?.price || 0) + totalExtraFee;

  if (loading) {
    return (
      <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-body text-center py-5">
              <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isDelivery = formData.orderType.toLowerCase() === 'delivery' || formData.orderType.toLowerCase().includes('delivery');

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: 800 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">
              {readOnly ? 'Order Details' : (sale ? 'Edit Sale' : 'New Sale')}
            </h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            {/* Status Banner */}
            {readOnly && sale && (
              <div className="mb-3">
                {(() => {
                  const status = getSaleStatus(sale);
                  const colors = {
                    danger: { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' },
                    success: { bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
                    warning: { bg: '#fff3cd', text: '#856404', border: '#ffeeba' },
                    dark: { bg: '#d6d8d9', text: '#1b1e21', border: '#c6c8ca' },
                  };
                  const c = colors[status.variant] || colors.warning;
                  return (
                    <div>
                      <div className="d-flex align-items-center justify-content-center p-3" style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12 }}>
                        <i className={`fas ${status.variant === 'danger' || status.variant === 'dark' ? 'fa-undo' : status.variant === 'success' ? 'fa-check-circle' : 'fa-clock'} mr-2`} style={{ color: c.text }}></i>
                        <span className="font-weight-bold" style={{ color: c.text, fontSize: '1.1rem' }}>{status.label}</span>
                      </div>
                      {sale.type === 'refund' && sale.originalSaleId && (
                        <div className="text-center mt-1">
                          <small className="text-muted">Original order: #{sale.originalSaleId}</small>
                        </div>
                      )}
                      {sale.type !== 'refund' && sale.refundStrategy && (
                        <div className="text-center mt-1">
                          <small className="text-muted">Refund strategy: {sale.refundStrategy === 'by_item' ? 'Refund by Item' : 'Refund by Amount'}</small>
                        </div>
                      )}
                      {sale.type !== 'refund' && (sale.refundedAmount || 0) > 0 && (
                        <div className="text-center mt-1">
                          <small className="text-muted">Refunded so far: {formatCurrency(sale.refundedAmount)}</small>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            {/* Fulfillment Status Banner */}
            {sale && (
              <div className="mb-3">
                {(() => {
                  const fs = sale.fulfillmentStatus || 'pending';
                  const meta = getFulfillmentStatusMeta(fs);
                  return (
                    <div className="p-3" style={{ background: meta.bg, border: `1px solid ${meta.border}`, borderRadius: 12 }}>
                      <div className="d-flex align-items-center justify-content-center">
                        <i className={`fas ${meta.icon} mr-2`} style={{ color: meta.color }}></i>
                        <span className="font-weight-bold" style={{ color: meta.color }}>
                          {meta.label}
                        </span>
                      </div>
                      {fs !== 'complete' && onFulfillmentUpdate && (
                        <div className="d-flex mt-2" style={{ gap: 8 }}>
                          <button
                            className="btn btn-sm btn-primary font-weight-bold flex-fill"
                            style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 8 }}
                            onClick={() => onFulfillmentUpdate(sale)}
                          >
                            <i className="fas fa-edit mr-2"></i>Update Fulfillment Status
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            {/* Delivery (Uber Direct) banner — visible on the order detail so
                staff can answer "did the courier get dispatched / accept?":
                dispatch happens automatically when the order turns 'ready';
                the webhook keeps courier + status + tracking up to date. */}
            {sale && sale.deliveryProvider === 'uber_direct' && (
              <div className="mb-3">
                {(() => {
                  const ds = (sale.deliveryStatus || '').toString();
                  const dispatched = !!sale.deliveryId;
                  const courierLabel = {
                    pending: 'Finding a courier…',
                    pickup: 'Courier assigned — heading to the restaurant',
                    pickup_imminent: 'Courier arriving at the restaurant',
                    pickup_complete: 'Picked up — on the way to the customer',
                    dropoff_imminent: 'Courier arriving at the customer',
                    delivered: 'Delivered',
                    canceled: 'Delivery canceled',
                    returned: 'Returned to the restaurant',
                  }[ds] || (dispatched ? ds.replace(/_/g, ' ') : null);
                  const fs = sale.fulfillmentStatus || 'pending';
                  return (
                    <div className="p-3" style={{ background: '#fff3ee', border: '1px solid rgba(255,87,34,0.35)', borderRadius: 12 }}>
                      <div className="d-flex align-items-center justify-content-center">
                        <i className="fas fa-motorcycle mr-2" style={{ color: '#ff5722' }}></i>
                        <span className="font-weight-bold" style={{ color: '#ff5722' }}>Delivery by Uber Direct</span>
                      </div>
                      <div className="text-center mt-2" style={{ fontSize: '0.85rem', color: '#555' }}>
                        {!dispatched && fs !== 'ready' && fs !== 'out_for_delivery' && fs !== 'complete' && (
                          <span><i className="fas fa-info-circle mr-1"></i>The courier is dispatched automatically when this order is marked <strong>Ready</strong>.</span>
                        )}
                        {!dispatched && fs === 'ready' && (
                          <span><i className="fas fa-spinner fa-spin mr-1"></i>Dispatching courier…</span>
                        )}
                        {dispatched && courierLabel && (
                          <span className="font-weight-bold" style={{ color: '#333' }}>{courierLabel}</span>
                        )}
                      </div>
                      {sale.deliveryCourierName && (
                        <div className="text-center mt-1" style={{ fontSize: '0.85rem', color: '#333' }}>
                          <i className="fas fa-user mr-1"></i>
                          <strong>{sale.deliveryCourierName}</strong>
                          {sale.deliveryCourierVehicle ? ` · ${sale.deliveryCourierVehicle}` : ''}
                        </div>
                      )}
                      {sale.deliveryTrackingUrl && (
                        <div className="text-center mt-2">
                          <a
                            href={sale.deliveryTrackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary font-weight-bold"
                            style={{ borderRadius: 8 }}
                          >
                            <i className="fas fa-map-marked-alt mr-1"></i>Track courier (live map)
                          </a>
                        </div>
                      )}
                      {(sale.deliveryAddress || sale.deliveryFee) && (
                        <div className="text-center mt-2" style={{ fontSize: '0.78rem', color: '#777' }}>
                          {sale.deliveryAddress && <div><i className="fas fa-map-marker-alt mr-1"></i>{sale.deliveryAddress}</div>}
                          {sale.deliveryFee > 0 && (
                            <div className="mt-1 p-2" style={{ background: 'rgba(255,87,34,0.08)', borderRadius: 8, textAlign: 'left' }}>
                              <i className="fas fa-info-circle mr-1" style={{ color: '#ff5722' }}></i>
                              The delivery fee ({formatCurrency(sale.deliveryFee)}) charged to the customer <strong>stays with FodyFee</strong> — it covers the Uber courier dispatched from FodyFee's account. Your payout for this order = <strong>products + tax + tip</strong> (minus the platform commission).
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            {/* Customer Section */}
            <div className="card mb-3" style={{ borderRadius: '12px' }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="font-weight-bold m-0">Customer</h6>
                  {!readOnly && (
                    <button className="btn btn-sm btn-outline-primary" onClick={() => setShowCustomerSelector(true)}>
                      {formData.isGuestCustomer ? 'Select' : 'Change'}
                    </button>
                  )}
                </div>
                {formData.isGuestCustomer || !formData.selectedCustomerId ? (
                  <>
                    <div className="form-group">
                      <label>Name *</label>
                      <input type="text" className="form-control" value={formData.customerName} readOnly={readOnly}
                        onChange={e => setFormData({ ...formData, customerName: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input type="email" className="form-control" value={formData.customerEmail} readOnly={readOnly}
                        onChange={e => setFormData({ ...formData, customerEmail: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input type="tel" className="form-control" value={formData.customerPhone} readOnly={readOnly}
                        onChange={e => setFormData({ ...formData, customerPhone: e.target.value })} />
                    </div>
                  </>
                ) : (
                  <div>
                    <div className="font-weight-bold">{formData.customerName}</div>
                    <div>{formData.customerEmail}</div>
                    <div className="text-muted">{formData.customerPhone}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Order Type & Payment */}
            <div className="card mb-3" style={{ borderRadius: '12px' }}>
              <div className="card-body">
                <h6 className="font-weight-bold mb-3">Order Type <span className="text-danger">*</span></h6>
                <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                  {orderTypes.map(ot => {
                    const isSelected = formData.orderType === ot.name;
                    return (
                      <button key={ot.id} type="button" disabled={readOnly}
                        className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ borderRadius: '16px', backgroundColor: isSelected ? '#ff6b35' : undefined, borderColor: isSelected ? '#ff6b35' : undefined }}
                        onClick={() => setFormData({ ...formData, orderType: ot.name })}>
                        {ot.name}
                      </button>
                    );
                  })}
                </div>

                {isDelivery && (
                  <div className="mt-4">
                    <h6 className="font-weight-bold mb-2">Delivery Address</h6>
                    {customerAddresses.length > 0 && !readOnly && (
                      <div className="mb-2">
                        <small className="text-muted">Saved Addresses:</small>
                        <div className="d-flex flex-wrap mt-1" style={{ gap: '8px' }}>
                          {customerAddresses.map(addr => (
                            <button key={addr.id} type="button" className="btn btn-sm btn-outline-info"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                deliveryAddress: addr.address || '',
                                deliveryCity: addr.city || '',
                                deliveryState: addr.state || '',
                                deliveryZipCode: addr.zipCode || '',
                                deliveryCountry: addr.country || '',
                                deliveryLatitude: addr.latitude || null,
                                deliveryLongitude: addr.longitude || null,
                              }))}>
                              {addr.address?.split(',')[0]}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <MapboxAddressInput
                      label="Address"
                      value={formData.deliveryAddress}
                      readOnly={readOnly}
                      onSelect={(addr) => setFormData(prev => ({
                        ...prev,
                        deliveryAddress: addr.streetAddress,
                        deliveryCity: addr.city || '',
                        deliveryState: addr.state || '',
                        deliveryZipCode: addr.zip || '',
                        deliveryCountry: addr.country || '',
                        deliveryLatitude: addr.latitude,
                        deliveryLongitude: addr.longitude,
                      }))}
                    />
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <input type="text" className="form-control" placeholder="City" readOnly={readOnly}
                          value={formData.deliveryCity} onChange={e => setFormData({ ...formData, deliveryCity: e.target.value })} />
                      </div>
                      <div className="col-md-6">
                        <input type="text" className="form-control" placeholder="State" readOnly={readOnly}
                          value={formData.deliveryState} onChange={e => setFormData({ ...formData, deliveryState: e.target.value })} />
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <input type="text" className="form-control" placeholder="Zip Code" readOnly={readOnly}
                          value={formData.deliveryZipCode} onChange={e => setFormData({ ...formData, deliveryZipCode: e.target.value })} />
                      </div>
                      <div className="col-md-6">
                        <input type="text" className="form-control" placeholder="Country" readOnly={readOnly}
                          value={formData.deliveryCountry} onChange={e => setFormData({ ...formData, deliveryCountry: e.target.value })} />
                      </div>
                    </div>
                  </div>
                )}

                <hr />
                <h6 className="font-weight-bold mb-2">Payment Type</h6>
                <div className="d-flex" style={{ gap: '8px' }}>
                  {['cash', 'card'].map(pt => {
                    const isSelected = formData.paymentType === pt;
                    return (
                      <button key={pt} type="button" disabled={readOnly}
                        className={`btn flex-fill ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ borderRadius: '8px', backgroundColor: isSelected ? '#ff6b35' : undefined, borderColor: isSelected ? '#ff6b35' : undefined }}
                        onClick={() => setFormData({ ...formData, paymentType: pt })}>
                        <i className={`fas ${pt === 'cash' ? 'fa-money-bill' : 'fa-credit-card'} mr-2`}></i>
                        {pt.charAt(0).toUpperCase() + pt.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Items Section */}
            <div className="card mb-3" style={{ borderRadius: '12px' }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="font-weight-bold m-0">Items <span className="text-danger">*</span></h6>
                  {!readOnly && (
                    <button className="btn btn-sm btn-outline-primary" onClick={() => setShowProductSelector(true)}>
                      <i className="fas fa-plus mr-1"></i>Add Product
                    </button>
                  )}
                </div>
                {items.length === 0 ? (
                  <div className="text-center py-3">
                    {!readOnly && (
                      <button className="btn btn-outline-primary" onClick={() => setShowProductSelector(true)}>
                        <i className="fas fa-plus mr-2"></i>Add Product
                      </button>
                    )}
                  </div>
                ) : (
                  items.map((item, idx) => (
                    <div key={idx} className="d-flex justify-content-between align-items-start py-2 border-bottom">
                      <div>
                        <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>{item.productName}</div>
                        {item.productDescription && <small className="text-muted d-block">{item.productDescription}</small>}
                        {item.options?.length > 0 && (
                          <small className="text-primary d-block">
                            Options: {item.options.map(o => typeof o === 'string' ? o : o.itemName || o.name).join(', ')}
                          </small>
                        )}
                        {item.instructions && <small className="text-info d-block"><i className="fas fa-comment mr-1"></i>{item.instructions}</small>}
                        <small>Qty: {item.qty} x {formatCurrency(item.price)}</small>
                        {item.fulfillmentStatus && (
                          <small className="d-block">
                            {(() => {
                              const meta = getFulfillmentStatusMeta(item.fulfillmentStatus);
                              const isCustom = meta.variant === 'teal' || meta.variant === 'cyan';
                              return (
                                <span
                                  className={`badge badge-${isCustom ? '' : meta.variant}`}
                                  style={{ fontSize: '0.65rem', cursor: 'pointer', ...(isCustom ? { backgroundColor: meta.bg, color: meta.color } : {}) }}
                                  onClick={() => handleUpdateItemFulfillment(idx)}
                                  title="Click to change item status"
                                >
                                  {meta.label} <i className="fas fa-pen" style={{ fontSize: '0.5rem', marginLeft: 2 }}></i>
                                </span>
                              );
                            })()}
                          </small>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-weight-bold">{formatCurrency(item.total)}</div>
                        {!readOnly && (
                          <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleRemoveItem(idx)}>
                            <i className="fas fa-minus-circle"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Totals Section */}
            <div className="card mb-3" style={{ borderRadius: '12px' }}>
              <div className="card-body">
                <div className="d-flex justify-content-between py-1"><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
                <div className="d-flex justify-content-between py-1"><span>Tax ({taxRate}%)</span><span>{formatCurrency(tax)}</span></div>

                <div className="mt-3">
                  <label className="font-weight-bold">Tip</label>
                  {!readOnly && (
                    <div className="d-flex flex-wrap mb-2" style={{ gap: '8px' }}>
                      {[10, 15, 20, 25].map(pct => (
                        <button key={pct} type="button"
                          className={`btn btn-sm ${formData.tipIsPercent && parseFloat(tipInput) === pct ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{ borderRadius: '16px', backgroundColor: formData.tipIsPercent && parseFloat(tipInput) === pct ? '#ff6b35' : undefined }}
                          onClick={() => { setFormData({ ...formData, tipIsPercent: true }); setTipInput(pct.toString()); }}>
                          {pct}%
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="d-flex align-items-center">
                    <input type="number" className="form-control form-control-sm" style={{ width: 100 }} readOnly={readOnly}
                      value={tipInput} placeholder={formData.tipIsPercent ? 'Custom %' : 'Custom $'}
                      onChange={e => { setTipInput(e.target.value); }} />
                    {!readOnly && (
                      <>
                        <span className="mx-2" style={{ color: formData.tipIsPercent ? '#ff6b35' : '#666' }}>%</span>
                        <div className="custom-control custom-switch mx-2">
                          <input type="checkbox" className="custom-control-input" id="tipToggle"
                            checked={!formData.tipIsPercent}
                            onChange={e => { setFormData({ ...formData, tipIsPercent: !e.target.checked }); setTipInput(''); }} />
                          <label className="custom-control-label" htmlFor="tipToggle"></label>
                        </div>
                        <span style={{ color: !formData.tipIsPercent ? '#ff6b35' : '#666' }}>$</span>
                      </>
                    )}
                    <span className="ml-auto font-weight-bold">{formatCurrency(calculatedTip)}</span>
                  </div>
                </div>

                {(sale?.convenienceFee || 0) > 0 && (
                  <div className="d-flex justify-content-between py-1 mt-2">
                    <span className="text-muted">Convenience Fee</span>
                    <span className="font-weight-bold">{formatCurrency(sale.convenienceFee)}</span>
                  </div>
                )}

                {(sale?.stripeFee || 0) > 0 && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted">Processing fee (Stripe)</span>
                    <span className="text-muted">− {formatCurrency(sale.stripeFee)}</span>
                  </div>
                )}
                {(sale?.applicationFeeAmount || 0) > 0 && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted">Platform fee (FodyFee)</span>
                    <span className="text-muted">− {formatCurrency(sale.applicationFeeAmount)}</span>
                  </div>
                )}

                <div className="mt-3">
                  <label className="font-weight-bold">Discount</label>
                  <div className="d-flex align-items-center">
                    <input type="number" className="form-control form-control-sm" style={{ width: 100 }} readOnly={readOnly}
                      value={discountInput} placeholder="0.00" onChange={e => setDiscountInput(e.target.value)} />
                    <span className="ml-auto text-danger">-{formatCurrency(discount)}</span>
                  </div>
                </div>

                <hr />
                <div className="d-flex justify-content-between font-weight-bold" style={{ fontSize: '1.1rem', color: '#ff6b35' }}>
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
            </div>

            {/* Split Checks Section */}
            {readOnly && splitChecks.length > 0 && (
              <div className="card mb-3" style={{ borderRadius: '12px', borderColor: '#17a2b8' }}>
                <div className="card-body">
                  <h6 className="font-weight-bold text-info mb-3"><i className="fas fa-code-branch mr-2"></i>Split Checks ({splitChecks.length})</h6>
                  {splitChecks.map((split, idx) => {
                    const splitTotal = (split.total || 0) + (split.tip || 0);
                    const isPaid = split.isPaid === true;
                    return (
                      <div key={split.id || idx} className="mb-2 p-2" style={{ background: isPaid ? '#d4edda' : '#e8f4f8', borderRadius: 8 }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{split.label || `Check ${idx + 1}`}</span>
                          <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>{formatCurrency(splitTotal)}</span>
                        </div>
                        <div className="d-flex justify-content-between mt-1">
                          <span style={{ fontSize: '0.75rem', color: '#666' }}>
                            {split.type === 'equal' ? 'Equal Split' : split.type === 'items' ? 'By Items' : 'By Amount'}
                            {split.items?.length > 0 && ` • ${split.items.length} items`}
                          </span>
                          <span style={{ fontSize: '0.75rem' }}>
                            {isPaid ? (
                              <span className="text-success font-weight-bold"><i className="fas fa-check mr-1"></i>Paid ({split.paymentMethod})</span>
                            ) : split.stripePaymentIntentId ? (
                              <span className="text-primary font-weight-bold"><i className="fas fa-credit-card mr-1"></i>Card Presented</span>
                            ) : (
                              <span className="text-muted">Pending</span>
                            )}
                          </span>
                        </div>
                        {(split.tip || 0) > 0 && (
                          <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.75rem' }}>
                            <span className="text-muted">Tip</span>
                            <span className="font-weight-bold">{formatCurrency(split.tip)}</span>
                          </div>
                        )}
                        {split.cardBrand && (
                          <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                            <i className="fas fa-credit-card mr-1"></i>{split.cardBrand} •••• {split.cardLast4}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="d-flex justify-content-between mt-2 pt-2" style={{ borderTop: '1px dashed #bee5eb' }}>
                    <span className="font-weight-bold" style={{ fontSize: '0.85rem' }}>Total from Splits</span>
                    <span className="font-weight-bold" style={{ fontSize: '0.9rem', color: '#17a2b8' }}>
                      {formatCurrency(splitChecks.reduce((sum, s) => sum + ((s.total || 0) + (s.tip || 0)), 0))}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Refunds Section */}
            {readOnly && refunds.length > 0 && (
              <div className="card mb-3" style={{ borderRadius: '12px', borderColor: '#dc3545' }}>
                <div className="card-body">
                  <h6 className="font-weight-bold text-danger mb-3"><i className="fas fa-undo mr-2"></i>Refunds</h6>
                  {refunds.map(refund => (
                    <div key={refund.id} className="mb-2 p-2" style={{ background: '#fff5f5', borderRadius: 8 }}>
                      <div className="d-flex justify-content-between">
                        <span className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{refund.refundType === 'by_item' ? 'Item Refund' : 'Amount Refund'}</span>
                        <span className="text-danger font-weight-bold">{formatCurrency(Math.abs(refund.total || 0))}</span>
                      </div>
                      <div className="text-muted small">
                        {refund.notes}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {refund.createdAt?.toDate ? refund.createdAt.toDate().toLocaleString() : new Date(refund.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {readOnly ? (
              <>
                <button type="button" className="btn btn-outline-primary" onClick={handleSendReceipt}>
                  <i className="fas fa-envelope mr-2"></i>Send Receipt
                </button>
                <button type="button" className="btn btn-outline-dark" onClick={handlePrintReceipt}>
                  <i className="fas fa-print mr-2"></i>Print Receipt
                </button>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="button" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={handleSave}>
                  {sale ? 'Update' : 'Create'} Sale
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Customer Selector Modal */}
      {showCustomerSelector && (
        <CustomerSelectorModal
          customers={customers}
          onSelect={handleSelectCustomer}
          onGuest={handleSetGuest}
          onClose={() => setShowCustomerSelector(false)}
        />
      )}

      {/* Product Selector Modal */}
      {showProductSelector && (
        <ProductSelectorModal
          products={products}
          onSelect={handleProductTap}
          onClose={() => setShowProductSelector(false)}
        />
      )}

      {/* Product Options Modal */}
      {showProductOptions && selectedProduct && (
        <ProductOptionsModal
          product={selectedProduct}
          options={relevantOptions}
          selectedOptions={selectedOptionsState}
          extraFees={extraFees}
          onChange={(opts, fees) => { setSelectedOptionsState(opts); setExtraFees(fees); }}
          onContinue={() => { setShowProductOptions(false); setShowQuantityDialog(true); }}
          onClose={() => setShowProductOptions(false)}
        />
      )}

      {/* Quantity Dialog */}
      {showQuantityDialog && selectedProduct && (
        <QuantityModal
          product={selectedProduct}
          extraFeeTotal={totalExtraFee}
          finalPrice={finalPrice}
          onAdd={(qty, instructions) => {
            const flatOptions = [];
            for (const [optId, namesSet] of Object.entries(selectedOptionsState)) {
              const optionDef = productOptions.find(o => o.id === optId);
              namesSet.forEach(name => {
                const optItem = optionDef?.options?.find(oi => oi.name === name);
                if (optItem) {
                  flatOptions.push({
                    optionId: optId,
                    optionName: optionDef?.name,
                    itemName: name,
                    extraFee: optItem.extraFee || 0,
                  });
                }
              });
            }
            handleAddProduct(selectedProduct, qty, flatOptions, totalExtraFee, instructions);
            setShowQuantityDialog(false);
            setSelectedProduct(null);
          }}
          onClose={() => { setShowQuantityDialog(false); setSelectedProduct(null); }}
        />
      )}
    </div>
  );
};

/* ==========================================
   CUSTOMER SELECTOR MODAL
   ========================================== */
const CustomerSelectorModal = ({ customers, onSelect, onGuest, onClose }) => {
  const [search, setSearch] = useState('');
  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">Select Customer</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <input type="text" className="form-control" placeholder="Search customers..."
                value={search} onChange={e => setSearch(e.target.value)} />
              <button className="btn btn-outline-secondary ml-2" onClick={onGuest}>Guest</button>
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {filtered.map(c => (
                <button key={c.id} className="list-group-item list-group-item-action py-2 text-left"
                  onClick={() => onSelect(c)}>
                  <div className="font-weight-bold">{c.name}</div>
                  <small className="text-muted">{c.email}</small>
                </button>
              ))}
              {filtered.length === 0 && <div className="text-center text-muted py-3">No customers found</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================================
   PRODUCT SELECTOR MODAL
   ========================================== */
const ProductSelectorModal = ({ products, onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const filtered = products.filter(p => p.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">Select Product</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <input type="text" className="form-control mb-3" placeholder="Search products..."
              value={search} onChange={e => setSearch(e.target.value)} />
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {filtered.map(p => (
                <div key={p.id} className="d-flex align-items-center p-2 border-bottom cursor-pointer"
                  style={{ cursor: 'pointer' }} onClick={() => onSelect(p)}>
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.name} style={{ width: 50, height: 50, borderRadius: 8, objectFit: 'cover' }} className="mr-3" />
                  ) : (
                    <div className="mr-3 d-flex align-items-center justify-content-center bg-light" style={{ width: 50, height: 50, borderRadius: 8 }}>
                      <i className="fas fa-box text-muted"></i>
                    </div>
                  )}
                  <div className="flex-grow-1">
                    <div className="font-weight-bold">{p.name}</div>
                    <small className="text-muted">{formatCurrency(p.price)}</small>
                  </div>
                  <i className="fas fa-plus-circle text-primary" style={{ fontSize: '1.2rem' }}></i>
                </div>
              ))}
              {filtered.length === 0 && <div className="text-center text-muted py-3">No products found</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================================
   PRODUCT OPTIONS MODAL
   ========================================== */
const ProductOptionsModal = ({ product, options, selectedOptions, extraFees, onChange, onContinue, onClose }) => {
  const toggleOption = (option, optItem) => {
    const newSelected = { ...selectedOptions };
    const newFees = { ...extraFees };

    if (option.allowMultipleSelection) {
      newSelected[option.id] = new Set(newSelected[option.id] || []);
      if (newSelected[option.id].has(optItem.name)) {
        newSelected[option.id].delete(optItem.name);
        delete newFees[optItem.name];
      } else {
        newSelected[option.id].add(optItem.name);
        newFees[optItem.name] = optItem.extraFee || 0;
      }
    } else {
      newSelected[option.id] = new Set([optItem.name]);
      Object.keys(newFees).forEach(k => {
        if (options.find(o => o.id === option.id)?.options?.some(oi => oi.name === k)) {
          delete newFees[k];
        }
      });
      newFees[optItem.name] = optItem.extraFee || 0;
    }

    onChange(newSelected, newFees);
  };

  const handleContinue = () => {
    for (const option of options) {
      if (option.optionRequired && (!selectedOptions[option.id] || selectedOptions[option.id].size === 0)) {
        Swal.fire('Error', `Please select required option: ${option.name}`, 'error');
        return;
      }
    }
    onContinue();
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">Select Options for {product.name}</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body" style={{ maxHeight: 400, overflowY: 'auto' }}>
            {product.description && <p className="text-muted">{product.description}</p>}
            {options.map(option => (
              <div key={option.id} className="mb-3">
                <div className="font-weight-bold mb-2">
                  {option.name}
                  {option.optionRequired && <span className="text-danger"> *</span>}
                </div>
                <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                  {option.options?.map(optItem => {
                    const isSelected = selectedOptions[option.id]?.has(optItem.name);
                    return (
                      <button key={optItem.name} type="button"
                        className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ borderRadius: '16px', backgroundColor: isSelected ? '#ff6b35' : undefined, borderColor: isSelected ? '#ff6b35' : undefined }}
                        onClick={() => toggleOption(option, optItem)}>
                        {optItem.name}{optItem.extraFee > 0 ? ` +${formatCurrency(optItem.extraFee)}` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={handleContinue}>Continue</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================================
   QUANTITY MODAL
   ========================================== */
const QuantityModal = ({ product, extraFeeTotal, finalPrice, onAdd, onClose }) => {
  const [qty, setQty] = useState(1);
  const [instructions, setInstructions] = useState('');

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1080 }}>
      <div className="modal-dialog modal-dialog-centered modal-sm">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">Add {product.name}</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <p className="text-muted">{formatCurrency(product.price)} base price</p>
            {extraFeeTotal > 0 && <p className="text-muted">+{formatCurrency(extraFeeTotal)} options</p>}
            <p className="font-weight-bold">Total: {formatCurrency(finalPrice)} each</p>
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" className="form-control" min="1" value={qty} onChange={e => setQty(parseInt(e.target.value) || 1)} />
            </div>
            {product.acceptSpecialRequirement && (
              <div className="form-group">
                <label>Special Instructions</label>
                <textarea className="form-control" rows="2" value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="Add any special requirements..." />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
              onClick={() => onAdd(qty, instructions)}>Add</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================================
   MAPBOX ADDRESS INPUT
   ========================================== */
const MapboxAddressInput = ({ label, value, readOnly, onSelect }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (val.length >= 3) {
        setIsSearching(true);
        const results = await searchMapboxAddress(val);
        setSuggestions(results);
        setIsSearching(false);
      } else {
        setSuggestions([]);
      }
    }, 500);
  };

  const handleSelect = (addr) => {
    setQuery(addr.streetAddress);
    setSuggestions([]);
    onSelect(addr);
  };

  return (
    <div className="form-group position-relative">
      <label>{label}</label>
      <div className="input-group">
        <div className="input-group-prepend"><span className="input-group-text"><i className="fas fa-search"></i></span></div>
        <input type="text" className="form-control" placeholder="Search for an address..."
          value={query} onChange={handleChange} readOnly={readOnly} />
        {isSearching && <div className="input-group-append"><span className="input-group-text"><i className="fas fa-spinner fa-spin"></i></span></div>}
      </div>
      {suggestions.length > 0 && (
        <div className="list-group position-absolute w-100" style={{ zIndex: 1090, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {suggestions.map(s => (
            <button key={s.id} type="button" className="list-group-item list-group-item-action py-2" onClick={() => handleSelect(s)}>
              <i className="fas fa-map-marker-alt text-primary mr-2"></i>
              <span className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{s.streetAddress}</span>
              <small className="text-muted d-block ml-4">{s.city}{s.city && s.state ? ', ' : ''}{s.state} {s.zip}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ==========================================
   STRIPE CARD FORM (Stripe Connect aware)
   ========================================== */
const StripeCardForm = ({ amount, user, locationId, feeEnabled, onSuccess, onError }) => {
  const [stripeInstance, setStripeInstance] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const stripeAccountId = userDoc.data()?.stripeAccountId || null;
        const platformKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
        const instance = stripeAccountId
          ? await loadStripe(platformKey, { stripeAccount: stripeAccountId })
          : await loadStripe(platformKey);

        if (cancelled) return;
        setStripeInstance(instance);

        // Original flow, unchanged: create the PaymentIntent up front (no
        // paymentMethodId yet) when this location doesn't have the
        // Convenience Fee enabled. When it IS enabled, we deliberately wait
        // — the card must be tokenized first so we can check card.funding
        // (credit vs debit) before deciding the final amount, since card
        // network rules only allow surcharging credit cards.
        if (!feeEnabled) {
          const functions = getFunctions();
          const createCardPaymentIntentFn = httpsCallable(functions, 'createCardPaymentIntent');
          const timestamp = new Date();
          const confirmationNumber = `WEB-${timestamp.getTime().toString().slice(-6)}`;
          const piResult = await createCardPaymentIntentFn({
            amount,
            currency: 'usd',
            description: `Web Sale #${confirmationNumber}`,
            locationId,
            metadata: { userId: user?.uid, confirmationNumber, locationId },
          });
          if (cancelled) return;
          setClientSecret(piResult.data.clientSecret);
          setPaymentIntentId(piResult.data.paymentIntentId);
        }
      } catch (e) {
        console.error('Error initializing card form:', e);
        onError(e.message || 'Failed to initialize payment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="text-center py-3">
        <span className="spinner-border spinner-border-sm mr-2"></span>
        Loading secure payment form...
      </div>
    );
  }

  if (!stripeInstance || (!feeEnabled && !clientSecret)) {
    return <div className="text-danger">Failed to load payment form</div>;
  }

  return (
    <Elements stripe={stripeInstance}>
      <StripeCardFormInner
        amount={amount}
        user={user}
        locationId={locationId}
        feeEnabled={feeEnabled}
        clientSecret={clientSecret}
        paymentIntentId={paymentIntentId}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
};

const StripeCardFormInner = ({ amount, user, locationId, feeEnabled, clientSecret, paymentIntentId, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (pmError) {
        onError(pmError.message || 'Invalid card details');
        setProcessing(false);
        return;
      }

      if (!feeEnabled) {
        // Original flow, unchanged: confirm the already-created
        // PaymentIntent directly with the tokenized payment method.
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethod.id,
        });

        if (error) {
          onError(error.message || 'Card payment failed');
        } else if (paymentIntent.status === 'succeeded') {
          onSuccess({
            paymentIntentId,
            cardBrand: paymentIntent.charges?.data?.[0]?.payment_method_details?.card?.brand,
            cardLast4: paymentIntent.charges?.data?.[0]?.payment_method_details?.card?.last4,
            amount,
            convenienceFeeAmount: 0,
          });
        } else if (paymentIntent.status === 'requires_action') {
          onError('This card requires additional authentication. Please use a different card or payment method.');
        } else {
          onError(`Payment status: ${paymentIntent.status}`);
        }
        setProcessing(false);
        return;
      }

      // Tokenize the card FIRST — this lets us check card.funding (credit
      // vs debit) server-side before deciding whether the Convenience Fee
      // applies, since card network rules only allow surcharging credit
      // cards, never debit/prepaid.
      const functions = getFunctions();
      const createCardPaymentIntentFn = httpsCallable(functions, 'createCardPaymentIntent');
      const timestamp = new Date();
      const confirmationNumber = `WEB-${timestamp.getTime().toString().slice(-6)}`;

      const piResult = await createCardPaymentIntentFn({
        amount,
        currency: 'usd',
        description: `Web Sale #${confirmationNumber}`,
        locationId,
        paymentMethodId: paymentMethod.id,
        metadata: {
          userId: user?.uid,
          confirmationNumber,
          locationId,
        },
      });

      const { clientSecret: newClientSecret, paymentIntentId: newPaymentIntentId, amount: chargedAmount, convenienceFeeAmount } = piResult.data;
      if (!newClientSecret || !newPaymentIntentId) {
        onError(piResult.data?.error || 'Failed to create payment intent');
        setProcessing(false);
        return;
      }

      // The backend already created AND confirmed the PaymentIntent using
      // the tokenized payment method above. Only a 3DS/other authentication
      // challenge (status == requires_action) needs further client handling.
      let finalStatus = piResult.data.status;
      if (finalStatus === 'requires_action') {
        const { error: actionError, paymentIntent: handled } = await stripe.confirmCardPayment(newClientSecret);
        if (actionError) {
          onError(actionError.message || 'Card authentication failed');
          setProcessing(false);
          return;
        }
        finalStatus = handled.status;
      }

      if (finalStatus === 'succeeded') {
        onSuccess({
          paymentIntentId: newPaymentIntentId,
          cardBrand: paymentMethod.card?.brand,
          cardLast4: paymentMethod.card?.last4,
          amount: chargedAmount,
          convenienceFeeAmount,
        });
      } else {
        onError(`Payment status: ${finalStatus}`);
      }
    } catch (e) {
      console.error('Card payment error:', e);
      onError(e.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <p className="text-muted small mb-2">
        <i className="fas fa-shield-alt mr-1"></i>
        Card details are entered securely via Stripe. Nothing is stored locally.
      </p>
      <div
        className="form-control mb-3"
        style={{ padding: '12px 16px', height: 'auto', borderRadius: 8 }}
      >
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      <button
        className="btn btn-primary btn-block btn-lg font-weight-bold"
        style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: '12px' }}
        onClick={handleSubmit}
        disabled={processing || !stripe}
      >
        {processing ? (
          <span className="spinner-border spinner-border-sm mr-2"></span>
        ) : (
          <i className="fas fa-check mr-2"></i>
        )}
        Complete Sale
      </button>
    </div>
  );
};

/* ==========================================
   CHECKOUT MODAL
   ========================================== */
const CheckoutModal = ({ sale, location, user, onComplete, onClose }) => {
  const [paymentType, setPaymentType] = useState('cash');
  const [processing, setProcessing] = useState(false);

  const finalizeCheckout = async (paymentTypeValue, stripeData = null) => {
    setProcessing(true);
    try {
      const db = getFirestore();
      const now = new Date();

      const pointsEarned = (sale.customerId && location?.pointsPerDollar > 0)
        ? Math.floor(sale.subtotal * location.pointsPerDollar)
        : 0;

      // For card payments, the backend is the authority on the Convenience
      // Fee (it checked card.funding — credit vs debit — and only applies
      // the fee for credit cards) — reconcile with what it actually
      // charged, so what gets persisted always matches the real Stripe
      // amount.
      const finalConvenienceFee = paymentTypeValue === 'card' ? (stripeData?.convenienceFeeAmount || 0) : 0;
      const finalTotal = paymentTypeValue === 'card' && typeof stripeData?.amount === 'number'
        ? stripeData.amount
        : sale.total;

      const saleData = {
        locationId: location.id,
        locationName: location.companyName,
        orderDate: sale.orderDate,
        orderStatus: 'completed',
        closeAt: now,
        customerId: sale.customerId,
        fodyesUid: sale.fodyesUid || null,
        customerName: sale.customerName,
        customerEmail: sale.customerEmail,
        customerPhone: sale.customerPhone,
        orderType: sale.orderType,
        confirmationNumber: sale.confirmationNumber,
        guestNumber: sale.guestNumber || null,
        paymentType: paymentTypeValue,
        cardType: paymentTypeValue === 'card' ? (stripeData?.cardBrand || 'debit') : null,
        cardLast4: paymentTypeValue === 'card' ? (stripeData?.cardLast4 || null) : null,
        stripePaymentIntentId: paymentTypeValue === 'card' ? (stripeData?.paymentIntentId || null) : null,
        itemsQty: sale.itemsQty,
        subtotal: sale.subtotal,
        tax: sale.tax,
        tip: sale.tip,
        tipPercent: sale?.tipPercent ?? 0,
        tipIsPercent: sale?.tipIsPercent ?? false,
        convenienceFee: finalConvenienceFee,
        discount: sale.discount,
        total: finalTotal,
        pointsEarned,
        createdAt: sale.orderDate,
        updatedAt: now,
      };

      // Save to root sales collection — SAME id as the location mirror
      // (a fresh random id here created a second, unlinked doc per charge:
      // refunds by mirror id missed the root, reports double-counted).
      const rootSaleRef = doc(db, 'users', user.uid, 'sales', sale.id);
      await setDoc(rootSaleRef, saleData, { merge: true });

      // Copy items to root sale
      if (sale.items?.length > 0) {
        await Promise.all(sale.items.map(item =>
          setDoc(doc(collection(db, 'users', user.uid, 'sales', sale.id, 'items')), item)
        ));
      }

      // Close the location sale
      await updateDoc(doc(db, 'users', user.uid, 'locations', location.id, 'sales', sale.id), {
        orderStatus: 'completed',
        closeAt: now,
        paymentType: paymentTypeValue,
        tipPercent: sale?.tipPercent ?? 0,
        tipIsPercent: sale?.tipIsPercent ?? false,
        convenienceFee: finalConvenienceFee,
        total: finalTotal,
        stripePaymentIntentId: paymentTypeValue === 'card' ? (stripeData?.paymentIntentId || null) : null,
        cardType: paymentTypeValue === 'card' ? (stripeData?.cardBrand || null) : null,
        cardLast4: paymentTypeValue === 'card' ? (stripeData?.cardLast4 || null) : null,
        fulfillmentStatus: sale.fulfillmentStatus || 'pending',
        fodyesUid: sale.fodyesUid || null,
        pointsEarned,
      });

      // Update customer reward points
      if (pointsEarned > 0) {
        await updateDoc(doc(db, 'users', user.uid, 'customers', sale.customerId), {
          rewardPoints: increment(pointsEarned),
        });
      }

      Swal.fire('Success', 'Payment processed successfully!', 'success');
      onComplete();
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'Failed to process payment', 'error');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">Checkout</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Order Summary */}
            <div className="card mb-3" style={{ borderRadius: '12px' }}>
              <div className="card-body">
                <h6 className="font-weight-bold">Order Summary</h6>
                <hr />
                <div className="d-flex justify-content-between py-1"><span>Order #</span><span className="font-weight-bold">{sale.confirmationNumber}</span></div>
                <div className="d-flex justify-content-between py-1"><span>Customer</span><span>{sale.customerName}</span></div>
                <div className="d-flex justify-content-between py-1"><span>Items</span><span>{sale.itemsQty}</span></div>
                <hr />
                <div className="d-flex justify-content-between py-1"><span>Subtotal</span><span>{formatCurrency(sale.subtotal)}</span></div>
                <div className="d-flex justify-content-between py-1"><span>Tax</span><span>{formatCurrency(sale.tax)}</span></div>
                <div className="d-flex justify-content-between py-1"><span>Tip</span><span>{formatCurrency(sale.tip)}</span></div>
                {sale.convenienceFee > 0 && <div className="d-flex justify-content-between py-1"><span>Convenience Fee</span><span>{formatCurrency(sale.convenienceFee)}</span></div>}
                <div className="d-flex justify-content-between py-1"><span>Discount</span><span className="text-danger">-{formatCurrency(sale.discount)}</span></div>
                <hr />
                <div className="d-flex justify-content-between font-weight-bold" style={{ fontSize: '1.1rem', color: '#ff6b35' }}>
                  <span>Total</span>
                  <span>{formatCurrency(sale.total)}</span>
                </div>
              </div>
            </div>

            {/* Payment Type */}
            <div className="card mb-3" style={{ borderRadius: '12px' }}>
              <div className="card-body">
                <h6 className="font-weight-bold mb-3">Payment Type</h6>
                <div className="d-flex" style={{ gap: '12px' }}>
                  {['cash', 'card'].map(pt => {
                    const isSelected = paymentType === pt;
                    return (
                      <button key={pt} type="button"
                        className={`btn flex-fill ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ borderRadius: '8px', backgroundColor: isSelected ? '#ff6b35' : undefined, borderColor: isSelected ? '#ff6b35' : undefined }}
                        onClick={() => setPaymentType(pt)}>
                        <i className={`fas ${pt === 'cash' ? 'fa-money-bill' : 'fa-credit-card'} mr-2`}></i>
                        {pt.charAt(0).toUpperCase() + pt.slice(1)}
                      </button>
                    );
                  })}
                </div>

                {paymentType === 'card' && (
                  <div className="mt-3">
                    <StripeCardForm
                      amount={sale.total}
                      user={user}
                      locationId={location?.id}
                      feeEnabled={location?.convenienceFee?.enabled === true}
                      onSuccess={(result) => finalizeCheckout('card', result)}
                      onError={(msg) => Swal.fire('Error', msg, 'error')}
                    />
                  </div>
                )}
              </div>
            </div>

            {paymentType === 'cash' && (
              <button className="btn btn-primary btn-lg btn-block" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                onClick={() => finalizeCheckout('cash')} disabled={processing}>
                {processing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Processing...</> : `Process Payment - ${formatCurrency(sale.total)}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
};

export default SalesPage;
